import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AdvisoryRowRota } from "@/components/AdvisoryRows";
import { api, formatError } from "@/lib/api";
import EmployeeSkillsLine from "@/components/EmployeeSkillsLine";
import { Avatar } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, CalendarDays, AlertTriangle, AlertCircle, X, Plus, MoreHorizontal, Lock, Unlock, Send, Download, Copy, FileText, Repeat, Info, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { resolveBankHolidayTheme } from "@/lib/bankHolidayTheme";
import { isPaidStarter, hasGrowthAccess } from "@/lib/effectivePlan";
import { canRosterWriteAction, canRosterTemplateAction, canUseRotaStylePattern, canEditRoster, canPublishRoster, isTeamAdmin, teamScopeIds, isMultiTeamAdmin } from "@/lib/teamAdminPerms";
import { buildTimeline, autoBreakStartHHMM } from "@/lib/timeline";
// TP-AT (Assignment Timing) -- start/end clock-time inputs for
// each assignment, with auto-derived duration. Backend still
// stores minutes as the canonical reporting field.
import {
  addMinutesToHHMM, formatHM,
  entryMinutesFromTimes, findOverlaps,
} from "@/lib/timing";
// Phase 2Z.n — Coverage Guard V1.
import { CoverageGuardBadge } from "@/components/CoverageGuardBadge";
import { CoverageGuardDrawer } from "@/components/CoverageGuardDrawer";
import { useCoverageGuard } from "@/hooks/useCoverageGuard";
import {
  ruleUsesUnsupportedV1Fields, summariseRosterCoverage,
} from "@/lib/coverageGuard";
// TP-AT.RS.8.2 (Feb 2026) -- shared apply-template helper used
// by the Roster cell editor, Bulk Add and the Rota Style cell
// editor. Central location so all three surfaces stay in sync.
import { deriveBlankWorkGapEntries } from "@/lib/deriveBlankWorkGaps";
// Undo/Redo V1 (Feb 2026) -- simple in-memory stacks for single cell
// edits on the visible week only. Restores replay the existing
// PUT /shifts + PUT /allocations write paths, so every permission,
// team scope, lock and validation rule applies unchanged.
import { useGridUndo } from "@/hooks/useGridUndo";
import { UndoRedoButtons, UndoRedoConfirmDialog } from "@/components/GridUndoRedo";
import { captureRosterCell, projectRosterSnap, applyRosterSnapshot } from "@/lib/gridUndo";
// Phase 2Z.ao — direct pattern generation from Roster (no popup).
// Reuses the existing POST /drafts/rota/run-from-style endpoint.

const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function mondayOf(date) {
  const d = new Date(date);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  d.setHours(0,0,0,0);
  return d;
}
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const minsToHM = (m) => `${Math.floor(m/60)}h${m%60 ? " " + (m%60) + "m" : ""}`;

export default function RosterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // TP-1 -- team_admin role is allowed in the role list (so future
  // phases can flip the helper without changing this line), but for
  // V1 the team-admin write capability is gated by the perms helper
  // and resolved later via `effectiveCanEdit`. company_admin /
  // super_admin behaviour is unchanged.
  const canEditByRole = ["company_admin","team_admin","super_admin"].includes(user.role);
  const isCompanyAdmin = user.role === "company_admin" || user.role === "super_admin";
  // Phase 2Z V1 — `/app/roster?week=YYYY-MM-DD` deep-link support so
  // the "Edit draft in roster" button on the Rota Style review page
  // can land the admin on the exact week they just edited.
  const [searchParams] = useSearchParams();
  const weekParam = searchParams.get("week");
  const initialWeek = (() => {
    if (weekParam && /^\d{4}-\d{2}-\d{2}$/.test(weekParam)) {
      const d = new Date(weekParam + "T00:00:00");
      if (!isNaN(d.getTime())) return mondayOf(d);
    }
    return mondayOf(new Date());
  })();
  const [weekStart, setWeekStart] = useState(initialWeek);
  // Mobile-only: track whether the user has already scrolled the roster grid
  // horizontally so we can hide the "Swipe →" hint once they get the gesture.
  const rosterScrollRef = useRef(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [data, setData] = useState({ employees: [], shifts: [], allocations: [], conflicts: {}, bank_holidays: {}, period_status: "draft" });
  const [categories, setCategories] = useState([]);
  // TP-AT.RS.7 (Phase 1) -- full category list (Work + Away + Leave +
  // BankHoliday) is required for kind lookups when classifying entries
  // for the Work / Away footer split. The Work-only `categories` state
  // above is kept verbatim so the assignment dropdown only offers
  // valid Work categories. We never put Away in the dropdown.
  const [allCategories, setAllCategories] = useState([]);
  // TP-AT.RS.7.2 -- transient soft-refuse message when the manager
  // clicks `Add assignment` but no unassigned gap is left in the
  // shift envelope. Renders an inline amber hint, never blocks the
  // button, never disables it. Auto-clears after 4s.
  const [noTimeLeftMsg, setNoTimeLeftMsg] = useState(false);
  const [combos, setCombos] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [teams, setTeams] = useState([]);
  // Locations (only fetched/used when display toggle is ON).
  const [locations, setLocations] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({ show_locations_on_rota: false });
  const [teamFilter, setTeamFilter] = useState(() => {
    try { return sessionStorage.getItem("nextrota.rosterTeamFilter") || "all"; } catch { return "all"; }
  });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  // TP-AT -- "Advanced" disclosure inside the allocation modal
  // reveals the raw minutes input for power users and reporting
  // edge cases. Hidden by default so the customer-friendly start/
  // end clock-time view is what managers normally see.
  const [showAdvancedMinutes, setShowAdvancedMinutes] = useState(false);
  const [editOriginal, setEditOriginal] = useState(null);
  const [tplDialog, setTplDialog] = useState({ open: false, mode: null, target: null });
  const [tplName, setTplName] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Undo/Redo V1 -- visible-week stacks (max 20 steps). Cleared on
  // week or team-filter change; page refresh / navigation clears them
  // naturally because this is plain component state.
  const gridUndo = useGridUndo(20);
  const [urBusy, setUrBusy] = useState(false);
  const [urConfirm, setUrConfirm] = useState(null); // null | "undo" | "redo"

  // Phase 2Z.ae - lookup so the employee column can show a tiny
  // skills sub-line ("English, Spanish, Salesforce, +N"). One existing
  // /skills GET; no new backend route.
  const [skillNameById, setSkillNameById] = useState({});

  // Phase 2Z.aa — paid Starter customers do not see Growth/Command
  // entry points on the Roster page (Coverage Guard badge, etc).
  // Core manual rota tools all remain available.
  const [billingMe, setBillingMe] = useState(null);
  useEffect(() => {
    api.get("/billing/me").then(r => setBillingMe(r.data)).catch(() => {});
  }, []);
  const starterTrim = billingMe && isPaidStarter(billingMe);
  // TP-1 -- centralised team-admin permission resolution. canEdit
  // gates every inline edit affordance (Add assignment, allocation
  // editor, +Add buttons, drag/drop). canRosterWrite gates Bulk
  // apply + Copy previous week. canRosterTemplates gates Save / Apply
  // week-template (kept Workspace Admin only until templates carry a
  // team_id). canUsePatternPicker keeps the existing growth-plan
  // gate AND adds the team-admin write gate; Team Admin path is
  // further restricted to their own team's profile inside
  // openRotaStylePatternFlow below.
  const canEdit = canEditByRole && canEditRoster(user, billingMe);
  const canRosterWrite = canRosterWriteAction(user, billingMe);
  const canRosterTemplates = canRosterTemplateAction(user, billingMe);
  const canUsePatternPicker = billingMe && hasGrowthAccess(billingMe)
    && canUseRotaStylePattern(user, billingMe);
  // TP-RS.team.2 Phase 3 -- direct Rota Style Apply (visible roster)
  // is gated by `roster_direct_rota_style_apply_enabled`. When the
  // flag is OFF for the tenant the Team Admin Use Rota Style menu
  // item is disabled with a clear message (Team Admin must never be
  // routed back into the legacy ghost-draft path); the Workspace
  // Admin keeps the legacy path so existing tenants are not blocked.
  const directApplyEnabled = !!(
    billingMe?.feature_flags?.roster_direct_rota_style_apply_enabled);

  // TP-3 -- Team Admin partial publish state.
  const [publishingTeam, setPublishingTeam] = useState(false);
  const [publishedTeamIds, setPublishedTeamIds] = useState([]);
  // TP-MultiTeamTA (Feb 2026) -- explicit picker dialog for multi-
  // team admins. Single-team admins keep the legacy confirm dialog
  // path. The picker is fired by the same Publish my team button.
  const [multiTeamPublishOpen, setMultiTeamPublishOpen] = useState(false);
  const [multiTeamPublishPicked, setMultiTeamPublishPicked] = useState(null);
  const canPublishTeam = isTeamAdmin(user) && canPublishRoster(user, billingMe);
  // TP-MultiTeamTA (Feb 2026) -- the full list of teams a TA can
  // act on. Empty array for non-TA users (callers branch on length).
  const myTeamIds = useMemo(() => teamScopeIds(user), [user]);
  // TP-3 + TP-MultiTeamTA -- a multi-team TA is considered "published
  // here" only when EVERY one of their assigned teams is in the
  // published set; otherwise they still have work to do. Single-team
  // TAs keep the legacy behaviour (one team in the set).
  const teamPublishedHere = myTeamIds.length > 0
    ? myTeamIds.every((tid) => publishedTeamIds.includes(tid))
    : publishedTeamIds.includes(user?.team_id || "__none__");
  const teamNameById = useMemo(
    () => Object.fromEntries((teams || []).map(t => [t.team_id, t.name])),
    [teams]);

  // TP-MultiTeam.grouping (Feb 2026) -- the Roster grid groups
  // employees by team header for any caller that legitimately spans
  // 2+ teams in the current view. Cases:
  //   - Workspace Admin with the "All teams" filter (teamFilter === "all").
  //   - Multi-team Team Admin (myTeamIds.length >= 2).
  // Single-team views render flat (legacy, byte-stable). Grouping
  // never widens the visible set -- it only re-sorts what
  // `/api/roster` already returned, so it cannot leak unassigned
  // teams.
  const groupRosterByTeam = useMemo(() => {
    if (isCompanyAdmin && teamFilter === "all" && (teams?.length || 0) > 1) {
      return true;
    }
    if (user?.role === "team_admin" && myTeamIds.length >= 2) {
      return true;
    }
    return false;
  }, [isCompanyAdmin, teamFilter, teams, user?.role, myTeamIds]);

  const rosterRows = useMemo(() => {
    const emps = data?.employees || [];
    if (!groupRosterByTeam) {
      // Flat list, sorted by name (legacy behaviour).
      return emps
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((emp) => ({ kind: "emp", emp }));
    }
    // Group by team_id; team order: by team name asc.
    const groups = new Map();
    for (const emp of emps) {
      const tid = emp.team_id || "_no_team";
      if (!groups.has(tid)) groups.set(tid, []);
      groups.get(tid).push(emp);
    }
    const ordered = Array.from(groups.entries())
      .map(([tid, list]) => ({
        team_id: tid,
        team_name: teamNameById?.[tid] || (tid === "_no_team" ? "No team" : tid),
        employees: list.slice().sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")),
      }))
      .sort((a, b) => a.team_name.localeCompare(b.team_name));
    const out = [];
    for (const g of ordered) {
      out.push({
        kind: "team_header",
        team_id: g.team_id,
        team_name: g.team_name,
        count: g.employees.length,
      });
      for (const emp of g.employees) {
        out.push({ kind: "emp", emp });
      }
    }
    return out;
  }, [data?.employees, groupRosterByTeam, teamNameById]);

  // TP-RS.team.1.2 - Workspace Admin "Publish week" scope dialog.
  // Opens whenever a Workspace Admin clicks Publish from the Roster
  // grid Actions menu on a multi-team tenant. The dialog gives two
  // simple choices:
  //   - All teams: existing PUT /roster-periods status=published
  //     path (byte-stable).
  //   - Italy/UK only: calls the SAME endpoint Team Admin uses
  //     (POST /roster-periods/{week}/publish-team), so selected-
  //     team publish from the Workspace Admin behaves identically
  //     to a Team Admin publishing their own team.
  // Hidden when teams list <= 1 (single-team tenants -> direct
  // PUT, no UX regression).
  const [publishScopeOpen, setPublishScopeOpen] = useState(false);
  const [publishScope, setPublishScope] = useState("all");
  const teamCount = Object.keys(teamNameById || {}).length;
  const openPublishFlow = () => {
    if (teamCount > 1) {
      setPublishScope("all");
      setPublishScopeOpen(true);
    } else {
      // Single-team or zero-team tenants - byte-stable legacy path.
      setStatus("published");
    }
  };
  const confirmPublishScope = async () => {
    if (publishScope === "all") {
      setPublishScopeOpen(false);
      await setStatus("published");
      return;
    }
    // Selected-team publish: same call Team Admin makes.
    const team_id = publishScope;
    const tname = teamNameById?.[team_id] || "team";
    setPublishScopeOpen(false);
    if (publishingTeam) return;
    setPublishingTeam(true);
    const toastId = toast.loading(`Publishing ${tname}…`);
    try {
      const { data } = await api.post(
        `/roster-periods/${iso(weekStart)}/publish-team`,
        { team_id, confirm: true });
      if (data?.status === "already_published") {
        toast.info(`${tname} was already published for this week.`,
          { id: toastId });
      } else if (data?.newly_fully_published) {
        toast.success(
          `Published ${data?.shift_count || 0} shifts for ${tname}. The roster is now fully published.`,
          { id: toastId, duration: 7000 });
      } else {
        toast.success(
          `Published ${data?.shift_count || 0} shifts for ${tname}.`,
          { id: toastId, duration: 6000 });
      }
      if (data?.team_id) {
        setPublishedTeamIds((prev) =>
          prev.includes(data.team_id) ? prev : [...prev, data.team_id]);
      }
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail) || `Could not publish ${tname}`,
        { id: toastId, duration: 8000 });
    } finally {
      setPublishingTeam(false);
    }
    try { await load(); } catch (_e) { /* already toasted on failure */ }
  };

  // TP-3.1 -- Workspace Admin amber "team in draft" derivation. Pure
  // frontend, no new backend state: a team is shown as "draft / in
  // progress" if there is at least one shift in the visible week for
  // an employee in that team AND that team is NOT already partial-
  // published. Excludes team_admins viewing their own team (they
  // already see the team-admin banner).
  const draftTeamIds = useMemo(() => {
    if (!data?.employees || !data?.shifts) return [];
    const teamByEmpId = Object.fromEntries(
      (data.employees || []).map(e => [e.employee_id, e.team_id]));
    const publishedSet = new Set(publishedTeamIds);
    const teamsWithShifts = new Set();
    for (const s of (data.shifts || [])) {
      const tid = teamByEmpId[s.employee_id];
      if (tid && !publishedSet.has(tid)) teamsWithShifts.add(tid);
    }
    return Array.from(teamsWithShifts);
  }, [data, publishedTeamIds]);

  // Phase 2Z.ao — direct in-place pattern generation (no popup).
  // Bumps a refresh key so the draft nudge re-fetches counts.
  const [generatingPattern, setGeneratingPattern] = useState(false);
  const [draftNudgeRefreshKey, setDraftNudgeRefreshKey] = useState(0);

  // Phase 2Z.n — Coverage Guard V1. Fetch active rules once, then on
  // every (rules ∪ visible-week) change, fan out to the existing
  // /api/coverage-rules/{id}/preview endpoint for one-week previews
  // and summarise into a tri-state verdict. NO backend changes.
  const [cgDrawerOpen, setCgDrawerOpen] = useState(false);
  const { rules: cgRules, reload: cgReload } = useCoverageGuard();
  const [cgVerdict, setCgVerdict] = useState({
    overall: "grey", perRule: [],
    totalEvaluatedCount: 0, unsupportedRulesCount: 0,
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // TP-CG.team — when the Roster is filtered to a specific team,
      // drop any Coverage Guard rule tied to a DIFFERENT team. A UK
      // rule must not show up as a warning / blocker while the user
      // is viewing the Italy roster. Company-wide rules (no team_id)
      // always remain in scope.
      const scopeTeamId = (teamFilter && teamFilter !== "all") ? teamFilter : null;
      const ruleInScope = (r) => {
        if (!r) return false;
        if (!scopeTeamId) return true;
        if (!r.team_id) return true;
        return r.team_id === scopeTeamId;
      };
      const activeV1 = (cgRules || []).filter(
        (r) => r.is_active && !ruleUsesUnsupportedV1Fields(r) && ruleInScope(r));
      const unsupported = (cgRules || []).filter(
        (r) => r.is_active && ruleUsesUnsupportedV1Fields(r) && ruleInScope(r));
      if (activeV1.length === 0 && unsupported.length === 0) {
        if (!cancelled) {
          setCgVerdict({
            overall: "grey", perRule: [],
            totalEvaluatedCount: 0, unsupportedRulesCount: 0,
          });
        }
        return;
      }
      // TP-CG.team — `apply_per_team` semantics. A company-wide rule
      // with `apply_per_team=true` must be evaluated SEPARATELY for
      // each team in scope. The backend preview endpoint accepts a
      // `team_id_override` query field that forces the eval to one
      // team. Fan that out here so the popover shows one row per team
      // (e.g. "1 customer support each team — Italy Helpdesk" goes
      // red while "— UK Helpdesk" stays green).
      const teamsInScope = scopeTeamId
        ? (teams || []).filter((t) => t.team_id === scopeTeamId)
        : (teams || []);
      const fetchTasks = [];
      for (const rule of activeV1) {
        // apply_per_team rule with no specific team_id -> expand per team.
        if (rule.apply_per_team && !rule.team_id && teamsInScope.length > 0) {
          for (const t of teamsInScope) {
            fetchTasks.push({ rule, teamForLabel: t, overrideTeamId: t.team_id });
          }
        } else {
          fetchTasks.push({ rule, teamForLabel: null, overrideTeamId: null });
        }
      }
      try {
        const previews = await Promise.all(
          fetchTasks.map(({ rule, teamForLabel, overrideTeamId }) => {
            const body = { on_date: iso(weekStart), window: "week" };
            if (overrideTeamId) body.team_id_override = overrideTeamId;
            return api.post(
              `/coverage-rules/${rule.coverage_id}/preview`, body,
            ).then(({ data }) => {
              // Decorate the rule for display so the popover shows
              // "<rule name> — <team name>" without changing the
              // underlying rule document. Use a unique synthetic
              // coverage_id so React keys stay stable.
              const displayRule = teamForLabel
                ? {
                    ...rule,
                    coverage_id: `${rule.coverage_id}::team::${teamForLabel.team_id}`,
                    name: `${rule.name || "Rule"} - ${teamForLabel.name}`,
                  }
                : rule;
              return { rule: displayRule, preview: data, error: null };
            }).catch((err) => {
              const displayRule = teamForLabel
                ? {
                    ...rule,
                    coverage_id: `${rule.coverage_id}::team::${teamForLabel.team_id}`,
                    name: `${rule.name || "Rule"} - ${teamForLabel.name}`,
                  }
                : rule;
              return {
                rule: displayRule, preview: null,
                error: err?.response?.data?.detail || "Preview failed",
              };
            });
          }),
        );
        const unsupportedItems = unsupported.map(
          (rule) => ({ rule, preview: null,
            error: "Rule uses fields not supported in Coverage Guard V1 yet" }));
        if (cancelled) return;
        setCgVerdict(
          summariseRosterCoverage([...previews, ...unsupportedItems]));
      } catch {
        if (!cancelled) {
          setCgVerdict({
            overall: "grey", perRule: [],
            totalEvaluatedCount: 0, unsupportedRulesCount: 0,
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // `data` is in the deps so the verdict re-evaluates after every
    // roster load (shift edits, bulk apply, clears). Without it the
    // badge showed a stale result until the page was refreshed.
  }, [cgRules, weekStart, teamFilter, teams, data]);

  useEffect(() => {
    try { sessionStorage.setItem("nextrota.rosterTeamFilter", teamFilter); } catch {}
  }, [teamFilter]);

  const load = async () => {
    try {
      const params = { week_start: iso(weekStart) };
      if (teamFilter && teamFilter !== "all") params.team_id = teamFilter;
      const [r, c, tp, tm, cb, st, ds, loc, sk] = await Promise.all([
        api.get("/roster", { params }),
        api.get("/categories"),
        canEdit ? api.get("/roster-templates") : Promise.resolve({ data: [] }),
        // TP-MultiTeamTA (Feb 2026) -- /teams is now fetched for
        // every role so multi-team admins see the correct team name
        // in banners and dialogs. The backend already RBAC-scopes
        // the response to the caller's `_team_admin_scope`, so this
        // does not leak any team outside their scope.
        api.get("/teams").catch(() => ({ data: [] })),
        canEdit ? api.get("/combos").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        // TP-AT.RS.8.1 (Feb 2026) -- fetch shift templates for
        // every logged-in role that reaches the Roster page.
        // The old `canEdit ?` gate caused a race: `canEdit`
        // depends on `billingMe` which loads AFTER this effect
        // fires, so Team Admins landed with an empty template
        // list and never saw the Apply template dropdown even
        // when their tenant enables `team_roster_edit_enabled`.
        // The endpoint is company-scoped and view-safe, so we
        // just always fetch. Applying still requires roster
        // cell edit permission (gated at the picker + backend).
        api.get("/shift-templates").catch(() => ({ data: [] })),
        api.get("/me/display-settings").catch(() => ({ data: { show_locations_on_rota: false } })),
        api.get("/locations").catch(() => ({ data: [] })),
        api.get("/skills?include_archived=false").catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setPublishedTeamIds(r.data.published_team_ids || []);
      setCategories(c.data.filter(x => (x.kind === "work" || x.kind === "away") && x.is_active));
      setAllCategories(c.data || []);
      setTemplates(tp.data);
      setTeams(tm.data);
      setCombos((cb.data || []).filter(x => x.is_active !== false));
      setShiftTemplates((st.data || []).filter(x => x.is_active !== false));
      setDisplaySettings(ds.data || { show_locations_on_rota: false });
      setLocations(loc.data || []);
      const skillMap = {};
      for (const s of (sk.data || [])) skillMap[s.skill_id] = s.name;
      setSkillNameById(skillMap);
      // Undo/Redo V1 -- return the fresh roster payload so save
      // handlers can capture an accurate "after" cell snapshot.
      return r.data;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart, teamFilter]);
  // Reset the "Swipe for more days" hint each time the user changes weeks so
  // the cue reappears for an unfamiliar layout. Cheap state - no flash, the
  // hint only ever paints on `sm:hidden` viewports.
  useEffect(() => { setHasScrolled(false); }, [weekStart, teamFilter]);
  // Undo/Redo V1 -- changing week or team filter invalidates every
  // recorded step (they belong to a different visible grid).
  useEffect(() => { gridUndo.clear(); setUrConfirm(null);
    // eslint-disable-next-line
  }, [weekStart, teamFilter]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const shiftMap = useMemo(() => Object.fromEntries(data.shifts.map(s => [`${s.employee_id}_${s.work_date}`, s])), [data.shifts]);
  const allocMap = useMemo(() => Object.fromEntries(data.allocations.map(a => [`${a.employee_id}_${a.work_date}`, a])), [data.allocations]);

  const totalForEmployee = (empId) =>
    data.allocations.filter(a => a.employee_id === empId)
      .reduce((sum, a) => sum + (a.entries || []).reduce((s, e) => s + (e.minutes||0), 0), 0);

  const capacityForEmployee = (empId) =>
    data.shifts.filter(s => s.employee_id === empId).reduce((sum, s) => sum + (s.total_minutes||0), 0);

  // TP-AT.RS.7 (Phase 1) -- Away helpers. The "Break" seeded category
  // is matched by `code === "break"` so it is stable across tenants.
  // `isAwayCategoryId` is used to classify entries when summing
  // Work / Away. `breakCategoryId` is the category that the editor
  // synthesises / writes Break entries against.
  const breakCategoryId = useMemo(() => {
    const c = allCategories.find(x => x.code === "break" && x.kind === "away");
    return c ? c.category_id : null;
  }, [allCategories]);
  const awayCategoryIds = useMemo(() => {
    const s = new Set();
    for (const c of allCategories) if (c.kind === "away") s.add(c.category_id);
    return s;
  }, [allCategories]);
  const isAwayEntry = (e) => !!e && awayCategoryIds.has(e.category_id);

  const openCell = (emp, day) => {
    if (!canEdit) return;
    const key = `${emp.employee_id}_${iso(day)}`;
    const shift = shiftMap[key];
    const alloc = allocMap[key];
    const wp = emp.working_pattern || {};
    const next = {
      employee: emp, work_date: iso(day),
      start_time: shift?.start_time ?? wp.default_start ?? "09:00",
      end_time: shift?.end_time ?? wp.default_end ?? "17:00",
      break_minutes: shift?.break_minutes ?? wp.default_break_minutes ?? 60,
      break_start_time: shift?.break_start_time ?? "",
      notes: shift?.notes ?? "",
      // Day-level location: prefer the existing allocation's
      // location, then the shift's, then blank.
      location_id: alloc?.location_id ?? shift?.location_id ?? "",
      hasShift: !!shift,
      // TP-AT -- when opening a legacy entry that was saved before
      // assignment timing existed, derive a sensible default
      // start_time / end_time from the shift window and the
      // entry's minutes so the time pickers are populated and
      // discoverable. Times that already exist on the entry are
      // preserved exactly. Running totals on subsequent entries
      // continue from the previous entry's end.
      entries: (() => {
        const shiftStart = shift?.start_time ?? wp.default_start ?? "09:00";
        const shiftEnd = shift?.end_time ?? wp.default_end ?? "17:00";
        let cursor = shiftStart;
        const hydrated = (alloc?.entries || []).map((e) => {
          const next = { ...e };
          if (!next.start_time) next.start_time = cursor;
          if (!next.end_time) {
            const mins = Number(next.minutes || 0);
            next.end_time = mins > 0
              ? (addMinutesToHHMM(next.start_time, mins) || shiftEnd)
              : shiftEnd;
          }
          cursor = next.end_time || cursor;
          return next;
        });
        // TP-AT.RS.7.4 -- DO NOT synthesise a Break Away entry from
        // legacy shift.break_minutes / break_start_time. The editor
        // is entries-only; managers add Break manually via the
        // normal Add assignment + Break (Away) dropdown. Old break
        // fields stay only as shadow compatibility (mirrored on
        // save). When the manager saves a cell with no Away rows,
        // saveShift writes break_minutes=0 / break_start_time=null
        // and the legacy data is cleaned naturally.
        // TP-AT.RS.SF1.1 - render entries chronologically. Legacy
        // cells saved before the entries-only model may have been
        // persisted in arbitrary insertion order; sort on hydrate
        // so the row list matches start_time order from the first
        // render.
        const t2mLocal = (t) => {
          if (!t || typeof t !== "string") return Infinity;
          const m = t.match(/^(\d{1,2}):(\d{2})$/);
          return m ? Number(m[1]) * 60 + Number(m[2]) : Infinity;
        };
        hydrated.sort((a, b) => {
          const sa = t2mLocal(a.start_time);
          const sb = t2mLocal(b.start_time);
          if (sa !== sb) return sa - sb;
          return t2mLocal(a.end_time) - t2mLocal(b.end_time);
        });
        return hydrated;
      })(),
      conflict: data.conflicts[key],
      is_bh: data.bank_holidays[emp.employee_id]?.includes(iso(day)),
    };
    setEdit(next);
    // Keep an immutable snapshot to detect unsaved changes on close.
    // IMPORTANT: this MUST mirror the projection used by isDirty() exactly -
    // any field missing on one side will make JSON.stringify diverge and trip
    // a false-positive "unsaved changes" prompt.
    setEditOriginal(JSON.stringify({
      start_time: next.start_time, end_time: next.end_time,
      break_minutes: String(next.break_minutes), break_start_time: next.break_start_time || "",
      notes: next.notes || "",
      location_id: next.location_id || "",
      entries: next.entries.map(e => ({
        category_id: e.category_id || "",
        combo_id: e.combo_id || null,
        minutes: String(e.minutes),
        // TP-AT -- include start_time / end_time in the snapshot so
        // the isDirty() check tracks clock-time edits too.
        start_time: e.start_time || "",
        end_time: e.end_time || "",
        note: e.note || "",
        location_id: e.location_id || "",
      })),
    }));
    setOpen(true);
  };

  /**
   * Split a combo entry into N atomic entries weighted by component percentages.
   * Minutes are snapped to 30-minute increments and any rounding remainder is
   * pushed onto the largest component so total minutes = round(input).
   */
  const expandComboEntry = (entry) => {
    const combo = combos.find(c => c.combo_id === entry.combo_id);
    if (!combo) return [];
    const totalMin = Math.max(0, Math.round(Number(entry.minutes || 0)));
    const slices = combo.components.map(comp => ({
      category_id: comp.category_id,
      raw: (totalMin * Number(comp.percentage)) / 100,
    }));
    // Round each to nearest 30. Then redistribute remainder.
    const snapped = slices.map(s => ({ ...s, minutes: Math.max(0, Math.round(s.raw / 30) * 30) }));
    let diff = totalMin - snapped.reduce((s, x) => s + x.minutes, 0);
    if (diff !== 0) {
      // adjust largest slice by `diff` (still multiple of 30)
      const idx = snapped.reduce((best, s, i) => s.raw > snapped[best].raw ? i : best, 0);
      snapped[idx].minutes = Math.max(0, snapped[idx].minutes + diff);
    }
    return snapped
      .filter(s => s.minutes > 0)
      .map(s => ({
        category_id: s.category_id,
        minutes: s.minutes,
        note: entry.note || null,
        combo_id: combo.combo_id,
        combo_label: combo.name,
      }));
  };

  /** True when the current `edit` differs from the snapshot taken in openCell.
   *  Projection MUST stay in lock-step with the snapshot in openCell(). */
  const isDirty = () => {
    if (!edit || !editOriginal) return false;
    const current = JSON.stringify({
      start_time: edit.start_time, end_time: edit.end_time,
      break_minutes: String(edit.break_minutes), break_start_time: edit.break_start_time || "",
      notes: edit.notes || "",
      location_id: edit.location_id || "",
      entries: edit.entries.map(e => ({
        category_id: e.category_id || "",
        combo_id: e.combo_id || null,
        minutes: String(e.minutes),
        start_time: e.start_time || "",
        end_time: e.end_time || "",
        note: e.note || "",
        location_id: e.location_id || "",
      })),
    });
    return current !== editOriginal;
  };

  /** Close handler - prompts when there are unsaved changes. */
  const requestClose = () => {
    if (isDirty()) {
      const ok = window.confirm("You have unsaved changes. Discard them and close?");
      if (!ok) return;
    }
    setOpen(false);
  };

  const saveShift = async () => {
    // TP-AT.RS.7 (Phase 1) -- mirror the Break Away entry (if any)
    // back into the legacy break_minutes / break_start_time fields
    // so every existing consumer (Coverage Guard, RSA seeder, RMI,
    // reports' calc_minutes, Dashboard, MyRoster) keeps working
    // unchanged. When no Break Away entry exists, write zero so
    // the shift no longer carries a stale break.
    const awayBreak = (edit.entries || []).find((e) => awayCategoryIds.has(e.category_id));
    const mirroredBreakMin = awayBreak ? Math.max(0, Number(awayBreak.minutes || 0)) : 0;
    const mirroredBreakStart = (awayBreak && mirroredBreakMin > 0)
      ? (awayBreak.start_time || null)
      : null;
    await api.put("/shifts", {
      employee_id: edit.employee.employee_id, work_date: edit.work_date,
      start_time: edit.start_time, end_time: edit.end_time,
      break_minutes: mirroredBreakMin,
      break_start_time: mirroredBreakStart,
      notes: edit.notes || null,
      location_id: edit.location_id || null,
    });
  };

  const saveAlloc = async () => {
    const userEntries = edit.entries.filter(e => !e.source_leave_id);
    // Expand combo entries into atomic rows tagged with combo_id before sending.
    const payload = [];
    for (const e of userEntries) {
      if (e.combo_id && !e.category_id) {
        payload.push(...expandComboEntry(e));
      } else {
        payload.push({
          category_id: e.category_id,
          minutes: Number(e.minutes),
          // TP-AT.RS.7 (Phase 1) -- persist clock times so a Break
          // Away block keeps its window across save/reopen. Backend
          // already accepts start_time / end_time on entries.
          start_time: e.start_time || null,
          end_time: e.end_time || null,
          note: e.note || null,
          combo_id: e.combo_id || null,
          combo_label: e.combo_label || null,
          location_id: e.location_id || null,
        });
      }
    }
    await api.put("/allocations", {
      employee_id: edit.employee.employee_id, work_date: edit.work_date,
      entries: payload,
      location_id: edit.location_id || null,
    });
  };

  const saveAll = async (e) => {
    e.preventDefault();
    // Validate user-edited entries before sending - never silently drop data
    const userEntries = edit.entries.filter(en => !en.source_leave_id);
    const invalid = userEntries.find(en => {
      const hasPicker = !!en.category_id || !!en.combo_id;
      return !hasPicker || !(Number(en.minutes) > 0);
    });
    if (invalid) {
      toast.error("Each assignment needs an activity (or combo) and minutes greater than 0. Fix or remove it before saving.");
      return;
    }
    // Undo/Redo V1 -- capture the cell before the write so the step
    // can be reverted. The "after" side is captured from the reloaded
    // grid so it matches exactly what the server stored.
    const urEmpId = edit.employee.employee_id;
    const urDate = edit.work_date;
    const urBefore = captureRosterCell(data, urEmpId, urDate);
    try {
      await saveShift(); await saveAlloc(); toast.success("Saved"); setOpen(false);
      const fresh = await load();
      if (fresh) {
        const urAfter = captureRosterCell(fresh, urEmpId, urDate);
        if (projectRosterSnap(urBefore) !== projectRosterSnap(urAfter)) {
          gridUndo.push({ employee_id: urEmpId, work_date: urDate, before: urBefore, after: urAfter });
        }
      }
    }
    catch (e) {
      if (e.response?.status === 423) toast.error("Week is locked - reopen it first or use lock override.");
      else toast.error(formatError(e.response?.data?.detail));
    }
  };

  const clearShift = async () => {
    const urEmpId = edit.employee.employee_id;
    const urDate = edit.work_date;
    const urBefore = captureRosterCell(data, urEmpId, urDate);
    try {
      await api.put("/shifts", { employee_id: urEmpId, work_date: urDate,
        start_time: null, end_time: null, break_minutes: 0, notes: null });
      await api.put("/allocations", { employee_id: urEmpId, work_date: urDate, entries: [] });
      toast.success("Cleared"); setOpen(false);
      const fresh = await load();
      if (fresh) {
        const urAfter = captureRosterCell(fresh, urEmpId, urDate);
        if (projectRosterSnap(urBefore) !== projectRosterSnap(urAfter)) {
          gridUndo.push({ employee_id: urEmpId, work_date: urDate, before: urBefore, after: urAfter });
        }
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Undo/Redo V1 -- apply the top stack record. Concurrency guard:
  // re-fetch the visible week and compare the target cell against the
  // snapshot we expect; if someone changed it since, ask first
  // (compare-and-warn, no backend versioning in V1). On any backend
  // refusal (403 / 423 / 404 / validation) the step stays available.
  const runGridHistory = async (mode, force = false) => {
    const rec = mode === "undo" ? gridUndo.peekUndo() : gridUndo.peekRedo();
    if (!rec || urBusy) return;
    setUrBusy(true);
    try {
      if (!force) {
        const params = { week_start: iso(weekStart) };
        if (teamFilter && teamFilter !== "all") params.team_id = teamFilter;
        const { data: freshGrid } = await api.get("/roster", { params });
        const current = captureRosterCell(freshGrid, rec.employee_id, rec.work_date);
        const expected = mode === "undo" ? rec.after : rec.before;
        if (projectRosterSnap(current) !== projectRosterSnap(expected)) {
          setUrConfirm(mode);
          return;
        }
      }
      const target = mode === "undo" ? rec.before : rec.after;
      await applyRosterSnapshot(rec.employee_id, rec.work_date, target);
      if (mode === "undo") gridUndo.commitUndo(); else gridUndo.commitRedo();
      toast.success(mode === "undo" ? "Undo applied" : "Redo applied");
      await load();
    } catch (e) {
      if (e.response?.status === 423) toast.error("Week is locked - reopen it first or use lock override.");
      else toast.error(formatError(e.response?.data?.detail)
        || "Could not apply the change. Your step is kept so you can retry.");
    } finally {
      setUrBusy(false);
    }
  };

  const updateEntry = (idx, field, val) => {
    const next = [...edit.entries];
    next[idx] = { ...next[idx], [field]: val };
    setEdit({...edit, entries: next});
  };
  /** Patch multiple fields at once on an entry (used when switching between
   * category and combo so the picker stays consistent). */
  const updateEntries = (idx, patch) => {
    const next = [...edit.entries];
    next[idx] = { ...next[idx], ...patch };
    setEdit({...edit, entries: next});
  };
  // TP-AT (Assignment Timing) -- writes start_time / end_time
  // and keeps `minutes` in sync. Customer-friendly: when only one
  // time changes, the other is auto-filled using the entry's
  // existing minutes value (so a typical "set start to 09:00 on a
  // 2h entry" instantly shows 11:00 as the end). Backend stays
  // defensive: if a partial entry slips through, the upsert keeps
  // the existing minutes -- it does NOT recompute.
  const setEntryTime = (idx, field, val) => {
    const cur = (edit.entries || [])[idx] || {};
    const next = { ...cur, [field]: val || null };
    const otherField = field === "start_time" ? "end_time" : "start_time";
    const otherVal = next[otherField];
    const curMins = Number(cur.minutes || 0);
    // Auto-fill the missing side using the existing duration when
    // we have one (helps managers think in clock times, not
    // minutes).
    if (val && !otherVal && curMins > 0) {
      const sign = field === "start_time" ? +1 : -1;
      const filled = addMinutesToHHMM(val, sign * curMins);
      if (filled) next[otherField] = filled;
    }
    // Recompute minutes locally when both times are now set.
    if (next.start_time && next.end_time) {
      // TP-AT.RS.7 (Phase 1) -- Away entries (Break) compute minutes
      // as wall time directly; entryMinutesFromTimes would subtract
      // overlap with itself and return 0.
      if (awayCategoryIds.has(next.category_id)) {
        const ss = next.start_time.split(":");
        const ee = next.end_time.split(":");
        const wall = (Number(ee[0])*60 + Number(ee[1]))
                   - (Number(ss[0])*60 + Number(ss[1]));
        if (wall > 0) next.minutes = wall;
      } else {
        const m = entryMinutesFromTimes({
          start: next.start_time,
          end: next.end_time,
          shiftStart: edit.start_time,
          shiftEnd: edit.end_time,
          breakMinutes: Number(edit.break_minutes || 0),
          breakStart: edit.break_start_time,
        });
        if (m != null && m > 0) next.minutes = m;
      }
    }
    const arr = [...edit.entries];
    arr[idx] = next;
    setEdit({ ...edit, entries: arr });
  };
  // TP-AT.RS.SF1.1 - assignment rows render in chronological order.
  // Used by openCell hydration AND addEntry (gap-fill + Smart Split
  // branches) so the visible row order always matches start_time.
  // Manual time-picker edits in updateEntries DO NOT auto-reorder
  // mid-keystroke (would jump input focus); the cell re-sorts on
  // next openCell. Tie-break: end_time asc. Falsy times sort last.
  const _sortEntriesByStart = (entries) => {
    const t2m = (t) => {
      if (!t || typeof t !== "string") return Infinity;
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      return m ? Number(m[1]) * 60 + Number(m[2]) : Infinity;
    };
    return [...(entries || [])].sort((a, b) => {
      const sa = t2m(a.start_time);
      const sb = t2m(b.start_time);
      if (sa !== sb) return sa - sb;
      return t2m(a.end_time) - t2m(b.end_time);
    });
  };
  const addEntry = (kindHint) => {
    // TP-AT.RS.SF2 -- Simple Fill V2. Always append the new row
    // immediately after the latest end_time across all current
    // entries. This is the simplest possible behaviour: the
    // manager builds the day top-to-bottom; Add assignment always
    // continues from the latest end. No first-gap search, no
    // largest-row split, no jumping. Break/Away rows are normal
    // assignment rows and never receive special treatment here.
    //
    //   - If the latest end is before the shift end:
    //         new row = latest_end .. shift_end
    //   - If the latest end already equals the shift end AND the
    //     latest-ending row is a Work-kind row long enough to halve
    //     safely: split that row in half and place the new blank
    //     row in the back half (toast confirms).
    //   - Otherwise: soft-refuse (existing amber hint, byte-stable).
    //
    // Blank/Away/unknown-category rows are NEVER split.
    //
    // TP-AT.RS.8 -- optional `kindHint` ("work" | "away") from the
    // new "+ Add work" / "+ Add away" quick buttons filters the
    // per-row activity picker so managers see only relevant
    // categories. When `kindHint === "away"` we also pre-select
    // the seeded "Break" Away category (if it exists on this
    // tenant) so the row is valid immediately. When `kindHint ===
    // "work"` we deliberately leave `category_id` blank so the
    // manager picks the correct Work activity for their business
    // (Retention / Kitchen / Care / ... differs per tenant).
    // The `_wanted_kind` field is a UI-only hint; it is not
    // persisted (saveAlloc's payload whitelist strips it).
    const awayBreak = (kindHint === "away")
      ? (categories || []).find(c => c.kind === "away"
          && c.code === "break" && c.is_active !== false) || null
      : null;
    const preselectedCatId = awayBreak ? awayBreak.category_id : "";
    const wantedKindTag = (kindHint === "work" || kindHint === "away")
      ? kindHint : undefined;
    const shiftStart = edit.start_time || "09:00";
    const shiftEnd = edit.end_time || "17:00";
    const t2m = (t) => {
      if (!t || typeof t !== "string") return null;
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      return m ? Number(m[1]) * 60 + Number(m[2]) : null;
    };
    const m2t = (n) => `${String(Math.floor((n % (24*60)) / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
    const sMin = t2m(shiftStart);
    // TP-P5-overnight (Feb 2026) -- an overnight shift envelope
    // (`end < start`) is valid; normalise end to end + 24h so the
    // "Add entry" auto-fill sizes the new blank row for the full
    // shift window instead of falling back to a 1-hour default.
    let eMin = t2m(shiftEnd);
    if (sMin != null && eMin != null && eMin < sMin) eMin += 24 * 60;
    if (sMin == null || eMin == null || eMin <= sMin) {
      // Invalid envelope -- fall back to a legacy 1-hour default so
      // the manager still sees a row to work with.
      const last = edit.entries[edit.entries.length - 1] || {};
      const defaultStart = last.end_time || shiftStart;
      const defaultEnd = addMinutesToHHMM(defaultStart, 60) || shiftEnd;
      setEdit({ ...edit, entries: _sortEntriesByStart([...edit.entries, {
        category_id: preselectedCatId, minutes: 60,
        start_time: defaultStart, end_time: defaultEnd,
        _wanted_kind: wantedKindTag,
      }]) });
      return;
    }

    // Find the latest end_time across valid entries. -1 sentinel
    // means there are no valid entries yet (empty cell).
    // TP-P5-overnight (Feb 2026) -- overnight entries (ee < s) are
    // also normalised so we don't pick an early "22:00" as latest.
    let latestEnd = -1;
    let latestRow = null;  // the entry that ends at latestEnd
    let latestIdx = -1;
    (edit.entries || []).forEach((e, i) => {
      if (!e.start_time || !e.end_time) return;
      const es = t2m(e.start_time);
      let ee = t2m(e.end_time);
      if (ee == null || es == null) return;
      if (ee < es) ee += 24 * 60;
      if (ee > latestEnd) {
        latestEnd = ee;
        latestRow = e;
        latestIdx = i;
      }
    });

    // Case A - room remains after the latest end (or cell is empty).
    if (latestEnd < eMin) {
      const anchorMin = latestEnd > sMin ? latestEnd : sMin;
      const defaultStart = m2t(anchorMin);
      const defaultEnd = shiftEnd;
      const mins = entryMinutesFromTimes({
        start: defaultStart,
        end: defaultEnd,
        shiftStart: edit.start_time,
        shiftEnd: edit.end_time,
        breakMinutes: Number(edit.break_minutes || 0),
        breakStart: edit.break_start_time,
      });
      setNoTimeLeftMsg(false);
      setEdit({ ...edit, entries: _sortEntriesByStart([...edit.entries, {
        category_id: preselectedCatId,
        minutes: Math.max(0, Number(mins || 0)),
        start_time: defaultStart,
        end_time: defaultEnd,
        _wanted_kind: wantedKindTag,
      }]) });
      return;
    }

    // Case B - latest end already equals shift end. Try to split
    // the latest-ending row, but only if it is a Work row that is
    // long enough to halve safely (>= 2 minutes). Away/Break,
    // blank, and unknown-kind rows are NEVER split.
    let eligible = false;
    if (latestRow && latestRow.category_id
        && !awayCategoryIds.has(latestRow.category_id)) {
      const cat = allCategories.find(
        (c) => c.category_id === latestRow.category_id);
      if (!cat || !cat.kind || cat.kind === "work") {
        eligible = true;
      }
    }
    if (eligible) {
      const s = t2m(latestRow.start_time);
      const span = latestEnd - s;
      if (s != null && span >= 2) {
        const mid = s + Math.floor(span / 2);
        const midHHMM = m2t(mid);
        const arr = [...(edit.entries || [])];
        const original = arr[latestIdx];
        const firstHalf = {
          ...original,
          end_time: midHHMM,
          minutes: entryMinutesFromTimes({
            start: original.start_time,
            end: midHHMM,
            shiftStart: edit.start_time,
            shiftEnd: edit.end_time,
            breakMinutes: Number(edit.break_minutes || 0),
            breakStart: edit.break_start_time,
          }),
        };
        const newBlankMins = entryMinutesFromTimes({
          start: midHHMM,
          end: original.end_time,
          shiftStart: edit.start_time,
          shiftEnd: edit.end_time,
          breakMinutes: Number(edit.break_minutes || 0),
          breakStart: edit.break_start_time,
        });
        arr[latestIdx] = firstHalf;
        arr.splice(latestIdx + 1, 0, {
          category_id: preselectedCatId,
          minutes: Math.max(0, Number(newBlankMins || 0)),
          start_time: midHHMM,
          end_time: original.end_time,
          _wanted_kind: wantedKindTag,
        });
        setNoTimeLeftMsg(false);
        setEdit({ ...edit, entries: _sortEntriesByStart(arr) });
        try {
          toast.success("Made room for the new assignment.");
        } catch { /* toast not critical */ }
        return;
      }
    }

    // No room and not eligible to split - soft-refuse.
    setNoTimeLeftMsg(true);
    if (typeof window !== "undefined") {
      window.clearTimeout(addEntry._tid);
      addEntry._tid = window.setTimeout(() => setNoTimeLeftMsg(false), 4000);
    }
  };
  const removeEntry = (idx) => setEdit({...edit, entries: edit.entries.filter((_, i) => i !== idx)});

  const editAllocated = useMemo(() =>
    (edit?.entries || []).reduce((s, e) => s + Number(e.minutes||0), 0), [edit]);
  // TP-AT.RS.7 (Phase 1) -- editCapacity is now GROSS shift wall.
  // The break is shown as a visible Away block (which contributes to
  // editAllocated alongside Work blocks), so subtracting break_minutes
  // here would double-deduct it.
  // TP-P5-overnight (Feb 2026) -- when end < start the shift crosses
  // midnight, so treat the difference as end + 24h - start so the
  // editor's "8h of 8h allocated" summary is honest for overnight
  // shifts. Same shape as the backend's `calc_minutes` helper.
  const editCapacity = useMemo(() => {
    if (!edit) return 0;
    if (!edit.start_time || !edit.end_time) return 0;
    const sh = edit.start_time.split(":"), eh = edit.end_time.split(":");
    const startMin = Number(sh[0])*60 + Number(sh[1]);
    const endMin = Number(eh[0])*60 + Number(eh[1]);
    const raw = endMin - startMin;
    return raw < 0 ? raw + 24*60 : raw;
  }, [edit]);
  // TP-AT.RS.7 (Phase 1) -- Work and Away minute totals for the footer
  // summary. Work = entries whose category.kind is "work" (or unknown
  // -- defaults to work for safety on uncategorised rows). Away =
  // entries whose category.kind is "away".
  const editWork = useMemo(() => {
    if (!edit) return 0;
    return (edit.entries || []).reduce((s, e) => {
      if (e.source_leave_id) return s; // leave entries handled by their own pipeline
      if (awayCategoryIds.has(e.category_id)) return s;
      return s + Math.max(0, Number(e.minutes || 0));
    }, 0);
  }, [edit, awayCategoryIds]);
  const editAway = useMemo(() => {
    if (!edit) return 0;
    return (edit.entries || []).reduce((s, e) => {
      if (e.source_leave_id) return s;
      if (!awayCategoryIds.has(e.category_id)) return s;
      return s + Math.max(0, Number(e.minutes || 0));
    }, 0);
  }, [edit, awayCategoryIds]);

  const shift = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n); setWeekStart(mondayOf(d)); };

  const setStatus = async (status) => {
    // TP-4 -- when a Workspace Admin publishes a week that has at
    // least one team already partial-published, surface a short
    // confirmation explaining the protection contract before the
    // PUT lands. Non-publish status changes (lock / draft) keep
    // their existing one-click behaviour.
    if (status === "published" && publishedTeamIds.length > 0) {
      const protectedNames = publishedTeamIds
        .map((tid) => teamNameById[tid] || "Team")
        .join(", ");
      const ok = window.confirm(
        `Some teams are already published. Published team cells will be protected and skipped (${protectedNames}). Remaining draft cells will be published.`,
      );
      if (!ok) return;
    }
    try {
      const { data } = await api.put("/roster-periods", { week_start: iso(weekStart), status });
      const wasPublished = status === "published" && !!data.published_at;
      // TP-4 -- prefer the structured `tp4` summary the backend now
      // returns when status === "published". Fall back to the
      // legacy short message for lock/draft and for older backends.
      if (wasPublished && data?.tp4) {
        const t = data.tp4;
        const protectedNames = (t.protected_teams || [])
          .map((x) => x.name)
          .join(", ");
        const newlyNames = (t.newly_published_teams || [])
          .map((x) => x.name)
          .join(", ");
        if (t.was_already_published) {
          toast.success(
            "Roster already published. All teams are protected and no duplicate shifts were created.",
            { duration: 6000 },
          );
        } else if (protectedNames && newlyNames) {
          toast.success(
            `Roster published. Protected teams were skipped: ${protectedNames}. Newly published teams: ${newlyNames}.`,
            { duration: 7000 },
          );
        } else if (protectedNames && !newlyNames) {
          toast.success(
            `Roster published. All teams were already protected: ${protectedNames}.`,
            { duration: 6000 },
          );
        } else if (!protectedNames && newlyNames) {
          toast.success(
            `Roster published for: ${newlyNames}. Employees notified by email.`,
            { duration: 6000 },
          );
        } else {
          toast.success("Week published - employees notified by email");
        }
      } else {
        toast.success(
          wasPublished
            ? "Week published - employees notified by email"
            : `Week ${status}`,
        );
      }
      // TP-4.2 -- optimistic local sync of publishedTeamIds so the
      // amber "Team draft in progress" banner disappears
      // immediately, even before the background load() completes.
      // The backend's TP-4.2 sync step has already $addToSet'd
      // every relevant active team onto roster_periods.published_team_ids,
      // so we mirror that in local state from the structured
      // `tp4` payload.
      if (wasPublished && data?.tp4) {
        const newIds = (data.tp4.newly_published_teams || [])
          .map((x) => x.team_id)
          .filter(Boolean);
        if (newIds.length) {
          setPublishedTeamIds((prev) => {
            const seen = new Set(prev);
            return [...prev, ...newIds.filter((t) => !seen.has(t))];
          });
        }
      }
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/export/roster.xlsx", { params: { week_start: iso(weekStart) }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `NextRota_Roster_${iso(weekStart)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get("/export/roster.pdf", { params: { week_start: iso(weekStart) }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `NextRota_Roster_${iso(weekStart)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // TP-3 -- Team Admin "Publish my team" handler. Calls the new
  // /roster-periods/{week_start}/publish-team endpoint. Stays on the
  // Roster page; the green badge replaces the button on success.
  //
  // TP-3.1 -- `load()` is intentionally outside the publish try/catch
  // so a transient refresh failure cannot surface as a fake publish
  // error. The publish itself is the only thing that can "fail" from
  // the user's point of view.
  //
  // TP-MultiTeamTA (Feb 2026) -- multi-team admins MUST pick which
  // team to publish via an explicit picker dialog. No silent fan-out.
  // Single-team admins keep the legacy direct-publish behaviour.

  // Inner helper: publish one specific team_id. Names the team in
  // every toast so a multi-team admin always knows which team was
  // published.
  const _publishOneTeam = async (teamId) => {
    if (!teamId) return null;
    if (publishingTeam) return null;
    const teamName = teamNameById?.[teamId] || "team";
    setPublishingTeam(true);
    const toastId = toast.loading(`Publishing ${teamName}…`);
    let publishResult = null;
    try {
      const { data } = await api.post(
        `/roster-periods/${iso(weekStart)}/publish-team`,
        { team_id: teamId, confirm: true });
      publishResult = data;
      if (data?.status === "already_published") {
        toast.info(`${teamName} was already published for this week.`,
          { id: toastId });
      } else if (data?.newly_fully_published) {
        // TP-4 -- this team's publish completed every publishable
        // team for the week, so the whole roster auto-flipped to
        // fully published.
        toast.success(
          `Published ${data?.shift_count || 0} shifts for ${teamName}. The roster is now fully published.`,
          { id: toastId, duration: 7000 });
      } else {
        toast.success(
          `Published ${data?.shift_count || 0} shifts for ${teamName}. The team has been notified.`,
          { id: toastId, duration: 6000 });
      }
      // Optimistic local update so the button flips immediately,
      // even if the background refresh below is slow.
      if (data?.team_id) {
        setPublishedTeamIds((prev) =>
          prev.includes(data.team_id) ? prev : [...prev, data.team_id]);
      }
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail) || `Could not publish ${teamName}`,
        { id: toastId });
      setPublishingTeam(false);
      return null;
    }
    setPublishingTeam(false);
    // Refresh in the background. Failures here are NOT publish
    // failures and must not show an error toast.
    try { await load(); } catch { /* silent - publish already succeeded */ }
    return publishResult;
  };

  const publishMyTeam = async () => {
    const scope = myTeamIds.length > 0
      ? myTeamIds
      : (user?.team_id ? [user.team_id] : []);
    if (scope.length === 0) return;
    if (publishingTeam) return;
    if (scope.length === 1) {
      // Single-team TA -- legacy direct-publish behaviour with a
      // confirm dialog. Wording stays "your team" because there is
      // only one team in play.
      const tname = teamNameById?.[scope[0]] || "your team";
      if (!window.confirm(
        `Publish ${tname}'s roster for this week? Employees will be notified by email.`
      )) return;
      return _publishOneTeam(scope[0]);
    }
    // Multi-team TA -- open the explicit picker dialog. NEVER fan out
    // silently. The dialog confirms which specific team is being
    // published.
    // TP-MultiTeamTA polish (Feb 2026) -- if any team's name is
    // missing (e.g. /teams loaded before assigned_team_ids was
    // mirrored to /auth/me), re-fetch /teams so chips can render the
    // proper team names instead of raw team_ids.
    const missing = scope.some((tid) => !teamNameById?.[tid]);
    if (missing) {
      try {
        const { data: freshTeams } = await api.get("/teams");
        if (Array.isArray(freshTeams)) setTeams(freshTeams);
      } catch { /* non-blocking; dialog still works with team_ids */ }
    }
    setMultiTeamPublishOpen(true);
  };

  const copyPrevWeek = async () => {
    const src = new Date(weekStart); src.setDate(src.getDate() - 7);
    try {
      const { data: r } = await api.post("/roster/copy", {
        source_week_start: iso(mondayOf(src)), target_week_start: iso(weekStart), overwrite,
      });
      toast.success(`Copied ${r.copied_shifts} shifts, ${r.copied_allocations} allocations` + (r.skipped_days ? ` · ${r.skipped_days} skipped` : ""));
      load();
    } catch (e) {
      if (e.response?.status === 423) {
        toast.error("Week is locked - reopen first or use lock override.");
      } else {
        toast.error(formatError(e.response?.data?.detail));
      }
    }
  };

  // TP-RS.team.1 -- Roster Actions ▶ "Use a Rota Style pattern" must
  // let the Workspace Admin choose which pattern to apply (company-
  // wide or any team-scoped). The legacy menu-item fired the API
  // straight away which always used the company-wide profile; the
  // selector below short-circuits that with a small inline dialog.
  const [patternScopeOpen, setPatternScopeOpen] = useState(false);
  const [patternProfiles, setPatternProfiles] = useState([]);
  const [patternScopeLoading, setPatternScopeLoading] = useState(false);
  // TP-RS.team.2 Phase 3 -- Replace / Cancel dialog. Opens whenever
  // the new direct-apply endpoint returns a 409 existing_visible_
  // roster. Cancel closes the dialog and fires NO backend call.
  // Replace re-runs the apply with replace=true. The dialog text is
  // strict UK English and never implies the change has shipped to
  // employees -- Apply is not Publish.
  const [replaceDialog, setReplaceDialog] = useState({
    open: false, profileId: null, teamId: null, weekStart: null,
    blockedTeamIds: [], teamName: "",
  });
  // TP-RS.team.2 Phase 3 -- validation error dialog. Opens whenever
  // the backend reports `status: blocked, reason: invalid_pattern`.
  // Lists each issue so the manager can fix the Rota Style profile
  // before retrying. No writes happen while this dialog is open.
  const [validationDialog, setValidationDialog] = useState({
    open: false, issues: [],
  });

  // Phase 2Z.ao — one-click "use Rota Style pattern" for the current
  // week. Two chained existing endpoints, no new backend logic:
  //   1. POST /drafts/rota/run-from-style  -> creates draft period
  //   2. POST /drafts/rota/{id}/publish?as_draft=true  -> lands the
  //      draft shifts into the Roster's draft layer so the manager
  //      can edit them inline (same action as the "Edit in roster
  //      draft" button on DraftsRotaReviewPage). NO employee emails,
  //      NO live publish. RSA / Automation paths untouched.
  // TP-RS.team.1 -- now accepts an optional `profileId` so the
  // Workspace Admin can target a specific team's profile or the
  // company-wide profile.
  // TP-RS.team.2 -- Team Admin path enabled. The Team Admin route
  // ALWAYS passes their own team_id into the publish-as-draft step
  // so the call goes through the partial-publish branch (which
  // accepts team_admin when `team_partial_publish_enabled` is on),
  // and never trips the "Workspace Admin only" gate. Toast copy is
  // recomputed from `gen.created_count` + `gen.skipped_blocks` so a
  // fully-protected published week shows a clear warning, not a
  // misleading success. The "Review draft" CTA is shown only to
  // Workspace Admin because the `/app/drafts/rota/:period_id` route
  // is restricted to company_admin / super_admin.
  // TP-RS.team.2 Phase 3 -- one-click "Use a Rota Style pattern" on
  // the visible roster. Two paths, gated by the company flag
  // `roster_direct_rota_style_apply_enabled`:
  //
  //   A. directApplyEnabled === true (Phase 3 path)
  //      -- Calls POST /api/roster/apply-rota-style which writes
  //      straight into shifts + allocations, bypassing the legacy
  //      hidden-draft layer entirely.
  //      -- Apply is NOT Publish. The toast copy makes this clear
  //      and never offers a Review draft CTA (there is no draft).
  //      -- 409 existing_visible_roster opens the Replace / Cancel
  //      dialog. Cancel sends NO backend request; Replace re-runs
  //      with replace=true.
  //      -- Per-team validation issues open the validation dialog.
  //      -- No writes happen until the manager confirms Replace.
  //
  //   B. directApplyEnabled === false  (legacy fallback)
  //      -- Workspace Admin keeps the previous hidden-draft path so
  //      tenants who have not enabled the flag are not broken.
  //      -- Team Admin is REFUSED here with a clear message; they
  //      must not be routed back into the ghost-draft flow.
  const applyRotaStylePattern = async (profileId = null, opts = {}) => {
    if (generatingPattern) return;
    const window_start = iso(weekStart);
    const isTA = isTeamAdmin(user);

    if (!directApplyEnabled) {
      if (isTA) {
        toast.error(
          "Use a Rota Style pattern is not enabled for your team yet. Ask a Workspace Admin to enable Rota Style Apply.",
          { duration: 6000 });
        return;
      }
      return _legacyApplyRotaStylePattern(profileId);
    }

    setGeneratingPattern(true);
    const replaceFlag = !!opts.replace;
    const toastId = toast.loading(
      replaceFlag
        ? "Replacing this week's roster with the Rota Style pattern…"
        : "Applying Rota Style pattern to this week's roster…");

    const body = { week_start: window_start, replace: replaceFlag };
    if (profileId) body.profile_id = profileId;
    if (isTA) {
      // TP-MultiTeamTA (Feb 2026) -- when the caller specifies a
      // team_id (e.g. openRotaStylePatternFlow derives it from the
      // picked profile's team_id), honour that. Otherwise default to
      // the legacy primary team_id for single-team admins. The
      // backend always re-validates `team_id` is in the TA's scope
      // via `_team_admin_scope` so an out-of-scope team is rejected
      // server-side regardless.
      const teamId = opts.teamId || user.team_id;
      if (teamId) body.team_id = teamId;
    }

    try {
      const { data } = await api.post("/roster/apply-rota-style", body);
      await load();
      setDraftNudgeRefreshKey((k) => k + 1);

      const teams = Array.isArray(data?.teams) ? data.teams : [];
      const blockedInvalid = teams.find(
        (t) => t?.status === "blocked" && t?.reason === "invalid_pattern");
      if (blockedInvalid) {
        toast.dismiss(toastId);
        setValidationDialog({
          open: true,
          issues: Array.isArray(blockedInvalid.issues)
            ? blockedInvalid.issues : [],
        });
        return;
      }

      const summary = data?.summary || {};
      const applied = Number(summary.applied_count || 0);
      const replaced = Number(summary.replaced_count || 0);
      const teamsSkipped = Number(summary.teams_skipped || 0);
      const allPublishedSkip = teams.length > 0
        && teams.every(
          (t) => t?.status === "skipped"
                  && t?.reason === "team_already_published");

      if (allPublishedSkip) {
        toast.warning
          ? toast.warning(
              "Pattern not applied. This week's roster is already published. Published rows are protected from overwrite.",
              { id: toastId, duration: 8000 })
          : toast(
              "Pattern not applied. This week's roster is already published.",
              { id: toastId, duration: 8000 });
        return;
      }

      if (applied === 0) {
        // Surface the real reasons rather than a vague "Nothing to
        // apply". Maps each team's reason to UK English so managers
        // know what to fix (e.g. seed a profile, unpublish the team).
        const reasonLabels = {
          team_already_published: "team already published for this week",
          team_already_published_in_period: "team already published for this week",
          no_pattern_for_team: "no Rota Style pattern set for this team",
          profile_team_mismatch: "selected pattern is for a different team",
          resolver_skipped: "pattern produced no rows for this week",
          no_cells: "pattern has no cells yet",
          window_already_has_period: "an old pending draft still covers this week",
          invalid_pattern: "pattern has validation issues",
        };
        const reasonCounts = {};
        for (const t of teams) {
          if (t?.status === "applied" && Number(t?.summary?.applied_count || 0) > 0) {
            continue;
          }
          const reason = t?.reason || "resolver_skipped";
          reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        }
        const reasonBits = Object.entries(reasonCounts).map(([code, n]) => {
          const label = reasonLabels[code] || code.replace(/_/g, " ");
          return teams.length > 1 ? `${n} ${n === 1 ? "team" : "teams"}: ${label}` : label;
        });
        const detailLine = reasonBits.length > 0
          ? ` Reason: ${reasonBits.join("; ")}.`
          : "";
        const message = (teams.length > 1)
          ? `Nothing was applied for this week.${detailLine}`
          : `Nothing was applied for this week.${detailLine}`;
        toast.warning
          ? toast.warning(message, { id: toastId, duration: 10000 })
          : toast(message, { id: toastId, duration: 10000 });
        return;
      }

      const replacedNote = replaced > 0
        ? ` (${replaced} existing shift${replaced === 1 ? "" : "s"} replaced)`
        : "";
      const skipNote = teamsSkipped > 0
        ? ` (${teamsSkipped} team${teamsSkipped === 1 ? "" : "s"} skipped, already published or no pattern)`
        : "";
      toast.success(
        `Rota Style applied to this week. ${applied} shift${applied === 1 ? "" : "s"} filled${replacedNote}${skipNote}. Apply is not Publish - nothing is sent to employees until you publish.`,
        { id: toastId, duration: 9000 });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.reason === "existing_visible_roster") {
        toast.dismiss(toastId);
        const blocked = Array.isArray(detail?.blocked_team_ids)
          ? detail.blocked_team_ids : [];
        const namedTeams = blocked
          .map((tid) => teamNameById[tid] || tid)
          .filter(Boolean);
        const teamName = namedTeams.length === 1
          ? namedTeams[0]
          : (namedTeams.length > 1
              ? `${namedTeams.length} teams (${namedTeams.join(", ")})`
              : "this team");
        setReplaceDialog({
          open: true,
          profileId,
          teamId: (isTA && (opts.teamId || user.team_id))
            ? (opts.teamId || user.team_id) : null,
          weekStart: window_start,
          blockedTeamIds: blocked,
          teamName,
        });
        return;
      }
      if (status === 503) {
        toast.error(
          "Rota Style Apply is not enabled for your company yet. Ask a Workspace Admin to enable it.",
          { id: toastId, duration: 8000 });
        return;
      }
      if (status === 403) {
        toast.error(
          "You do not have permission to apply Rota Style to this team.",
          { id: toastId, duration: 6000 });
        return;
      }
      if (status === 404) {
        toast.error(
          "No Rota Style pattern set up yet. Open Rota Style to build one.",
          { id: toastId, duration: 6000 });
        return;
      }
      toast.error(
        formatError(detail) || "Could not apply the Rota Style pattern.",
        { id: toastId });
    } finally {
      setGeneratingPattern(false);
    }
  };

  // Legacy hidden-draft path. Retained ONLY for Workspace Admin
  // tenants that have not flipped on the direct-apply flag yet.
  // Team Admin can never reach this branch (see the early-return
  // in applyRotaStylePattern above).
  const _legacyApplyRotaStylePattern = async (profileId = null) => {
    if (generatingPattern) return;
    const window_start = iso(weekStart);
    setGeneratingPattern(true);
    const toastId = toast.loading("Adding Rota Style pattern to roster draft…");

    const isTA = isTeamAdmin(user);

    // Step 2: send the generated draft into the roster draft layer.
    // For Team Admin we route through the team-scoped partial publish
    // branch by passing `team_id` so the publish endpoint accepts the
    // call. For Workspace Admin / super_admin the legacy no-team_id
    // call is preserved byte-stable.
    const sendIntoRosterDraft = async (period_id) => {
      const qs = isTA && user.team_id
        ? `?as_draft=true&team_id=${encodeURIComponent(user.team_id)}`
        : `?as_draft=true`;
      await api.post(
        `/drafts/rota/${period_id}/publish${qs}`,
        { confirm: true },
      );
    };

    // Build a humane outcome message from the generator response.
    // Three branches:
    //   (a) created > 0  -> success with count.
    //   (b) created == 0 AND every skipped block is
    //       `skipped_team_already_published` -> warning, published
    //       cells are protected.
    //   (c) created == 0 with mixed reasons -> neutral info copy.
    // For Team Admin we also strip the Review draft CTA because
    // they cannot enter `/app/drafts/rota/:period_id` (route is
    // role-gated to company_admin / super_admin).
    const buildOutcome = (gen) => {
      const created = Number(gen?.created_count || 0);
      const skipped = Array.isArray(gen?.skipped_blocks) ? gen.skipped_blocks : [];
      const skippedCount = Number(gen?.skipped_count || skipped.length || 0);
      const allPublished = skippedCount > 0
        && skipped.length > 0
        && skipped.every((b) => b?.reason === "skipped_team_already_published");
      const action = (!isTA && gen?.review_link) ? {
        label: "Review draft",
        onClick: () => navigate(gen.review_link),
      } : undefined;
      if (created === 0 && allPublished) {
        return {
          kind: "warning",
          message: "Pattern not applied. All cells for this week are already published. Published cells are protected from overwrite.",
          action,
        };
      }
      if (created === 0 && skippedCount > 0) {
        return {
          kind: "warning",
          message: "Pattern not applied. Existing roster data is protected; no draft cells could be added.",
          action,
        };
      }
      if (created === 0) {
        return {
          kind: "warning",
          message: "Nothing to add. The pattern produced no cells for this week.",
          action,
        };
      }
      // created > 0
      const skipNote = skippedCount > 0
        ? ` (${skippedCount} cell${skippedCount === 1 ? "" : "s"} skipped, already published or protected)`
        : "";
      return {
        kind: "success",
        message: `Pattern added to roster draft for this week. ${created} cell${created === 1 ? "" : "s"} added${skipNote}.`,
        action,
      };
    };

    // End-to-end: generate -> send into roster draft -> refresh.
    // TP-RS.team.2 -- accepts the toast id to update so the replace-
    // existing-draft branch reuses the "Replacing existing draft…"
    // loading toast for its final outcome (was previously dismissing
    // the replace toast and updating the original ack toast which
    // had already been dismissed -- the outcome toast then never
    // surfaced).
    const runFlow = async (force, outcomeToastId) => {
      const body = {
        window_start, force_regenerate: !!force,
      };
      if (profileId) body.profile_id = profileId;
      const { data: gen } = await api.post("/drafts/rota/run-from-style", body);
      if (!gen?.period_id) {
        throw new Error("Generate did not return a period id");
      }
      if (Number(gen.created_count || 0) > 0) {
        await sendIntoRosterDraft(gen.period_id);
      }
      // Refresh both the roster grid (so the new draft shifts render)
      // and the draft nudge counter.
      await load();
      setDraftNudgeRefreshKey((k) => k + 1);
      const outcome = buildOutcome(gen);
      const opts = { id: outcomeToastId, duration: 8000, action: outcome.action };
      if (outcome.kind === "success") {
        toast.success(outcome.message, opts);
      } else {
        toast.warning ? toast.warning(outcome.message, opts) : toast(outcome.message, opts);
      }
    };

    try {
      await runFlow(false, toastId);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.existing_period_id) {
        toast.dismiss(toastId);
        const confirmReplace = window.confirm(
          "A draft rota already exists for this week. Replace it with a fresh draft from your Rota Style pattern?"
        );
        if (confirmReplace) {
          const replaceToast = toast.loading("Replacing existing draft…");
          try {
            await runFlow(true, replaceToast);
          } catch (err2) {
            toast.error(
              formatError(err2.response?.data?.detail) || "Could not generate draft",
              { id: replaceToast }
            );
          }
        }
      } else if (status === 409 && /already has a live rota/i.test(detail?.message || "")) {
        // TP-RS.team.2 -- friendly copy for the live-rota guard. Backend
        // refuses to seed when the visible week has published shifts.
        toast.error(
          "This week already has a published rota. Published cells are protected. Pick another week or clear the live rota first.",
          { id: toastId, duration: 8000 }
        );
      } else if (status === 404) {
        toast.error(
          "No Rota Style pattern set up yet. Open Rota Style to build one.",
          { id: toastId, duration: 6000 }
        );
      } else {
        toast.error(
          formatError(detail) || "Could not add pattern to roster draft",
          { id: toastId }
        );
      }
    } finally {
      setGeneratingPattern(false);
    }
  };

  // TP-RS.team.1 / TP-RS.team.2 -- when the menu item is clicked,
  // fetch the available profiles and route based on role:
  //
  // Workspace Admin path (unchanged):
  //   (a) only company-wide profile exists -> fire legacy flow directly
  //   (b) at least one team-scoped profile exists -> open scope picker
  //
  // Team Admin path (TP-RS.team.2):
  //   - Filter profiles to ONLY the Team Admin's own team_id.
  //   - If exactly one team profile exists -> apply it directly.
  //   - If none -> toast "Build a Rota Style pattern for your team
  //     first." and bail. NEVER fall back to company-wide and NEVER
  //     open the cross-team picker.
  //   - If more than one (shouldn't happen today, defensive) -> show
  //     the picker pre-filtered to the Team Admin's own profiles.
  const openRotaStylePatternFlow = async () => {
    if (generatingPattern) return;
    setPatternScopeLoading(true);
    try {
      const { data } = await api.get("/rota-style/profiles");
      const profs = Array.isArray(data?.profiles) ? data.profiles : [];
      if (isTeamAdmin(user)) {
        // TP-MultiTeamTA (Feb 2026) -- a multi-team admin sees
        // profiles from ANY of their assigned teams; never company-
        // wide. With one profile we apply directly; with more than
        // one (whether across teams or across multiple profiles in
        // the same team) we open the picker pre-filtered to those
        // profiles so the manager makes an explicit choice.
        const scopeIds = myTeamIds.length > 0
          ? myTeamIds
          : (user.team_id ? [user.team_id] : []);
        const scopeSet = new Set(scopeIds);
        const teamProfs = profs.filter(
          (p) => p.team_id && scopeSet.has(p.team_id));
        if (teamProfs.length === 0) {
          toast.error(
            scopeIds.length > 1
              ? "Build a Rota Style pattern for at least one of your assigned teams first."
              : "Build a Rota Style pattern for your team first.",
            { duration: 6000 });
          return;
        }
        if (teamProfs.length === 1) {
          await applyRotaStylePattern(
            teamProfs[0].profile_id,
            { teamId: teamProfs[0].team_id });
          return;
        }
        // Multi-team admin OR multi-profile case -- show the picker
        // pre-filtered so the Team Admin can never see another
        // team's pattern or the company-wide one.
        setPatternProfiles(teamProfs);
        setPatternScopeOpen(true);
        return;
      }
      setPatternProfiles(profs);
      const teamScoped = profs.filter((p) => p.team_id);
      // Legacy short-circuit: when no team-scoped profiles exist,
      // skip the dialog and just apply the company-wide profile.
      if (teamScoped.length === 0) {
        const companyWide = profs.find((p) => !p.team_id);
        await applyRotaStylePattern(companyWide?.profile_id || null);
        return;
      }
      setPatternScopeOpen(true);
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail) || "Could not load patterns");
    } finally {
      setPatternScopeLoading(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    try {
      await api.post("/roster-templates", { name: tplName, source_week_start: iso(weekStart) });
      toast.success("Template saved");
      setTplDialog({ open: false, mode: null, target: null }); setTplName("");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const applyTemplate = async (templateId) => {
    try {
      const { data: r } = await api.post(`/roster-templates/${templateId}/apply`, {
        target_week_start: iso(weekStart), overwrite,
      });
      toast.success(`Applied: ${r.copied_shifts} shifts, ${r.copied_allocations} allocations`);
      load();
    } catch (e) {
      if (e.response?.status === 423) toast.error("Week is locked - reopen first.");
      else toast.error(formatError(e.response?.data?.detail));
    }
  };

  const status = data.period_status || "draft";
  const isLocked = status === "locked";

  // TP-3.2 / TP-4.1 -- friendlier status shown to the user without
  // changing backend semantics. Two cases:
  //   * status="draft" but at least one team is already published =>
  //     show "Partial" (was TP-3.2).
  //   * status="published" but at least one team still has draft/in-
  //     progress work that has not been team-published yet =>
  //     show "Partial" too. This handles the stale-status case
  //     where Italy team-published while it was the only team
  //     with shifts, the week auto-flipped to "published" (TP-4
  //     G1), and then `Use Rota Style pattern` or a manual +Add
  //     added UK draft shifts. Backend tries to demote in
  //     `_rsb_seed_draft_period` for the pattern-fill path, but
  //     this frontend guard guarantees the UI never shows a
  //     misleading fully-published pill while in-progress teams
  //     exist - regardless of how those draft shifts arrived.
  let displayStatus = status;
  const hasUnpublishedDraftWork = isCompanyAdmin
    && publishedTeamIds.length > 0
    && draftTeamIds.some((t) => !publishedTeamIds.includes(t));
  if (status === "draft") {
    if (isTeamAdmin(user) && teamPublishedHere) {
      displayStatus = "my_team_published";
    } else if (isCompanyAdmin && publishedTeamIds.length > 0) {
      displayStatus = "partial";
    }
  } else if (status === "published" && hasUnpublishedDraftWork) {
    displayStatus = "partial";
  }

  // TP-AT.RS.8.6 (Feb 2026) -- Roster top-bar refinement.
  // The existing Publish flow lives inside the Actions dropdown
  // for Workspace Admins in two situations:
  //   1. week is fully in "draft"
  //   2. week is "published" but a team has since added new draft
  //      work (`hasUnpublishedDraftWork`)
  // Both call `openPublishFlow`. We surface the same handler as a
  // visible primary CTA when either condition is true. No new
  // permission checks -- reuses `isCompanyAdmin` and the same
  // status guards the dropdown already uses.
  const canPublishNow = isCompanyAdmin && (
    status === "draft" || (status === "published" && hasUnpublishedDraftWork)
  );

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-3">
            <span>Planning</span>
            <StatusPill status={displayStatus}/>
          </div>
          <h1 className="font-display text-4xl mt-1">Weekly roster</h1>
          <p className="text-sm text-[#4B5773] mt-2">Shift + assignments + leave + conflicts in one view.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
            <Button variant="ghost" size="sm" data-testid="roster-prev-week" onClick={()=>shift(-7)}><ChevronLeft className="h-4 w-4"/></Button>
            <div className="text-sm font-medium flex items-center gap-2 px-2">
              <CalendarDays className="h-4 w-4 text-[#2563EB]"/>
              <span>Week of {iso(weekStart)}</span>
            </div>
            <Button variant="ghost" size="sm" data-testid="roster-next-week" onClick={()=>shift(7)}><ChevronRight className="h-4 w-4"/></Button>
            <Button variant="ghost" size="sm" data-testid="roster-today-btn" onClick={()=>setWeekStart(mondayOf(new Date()))}>Today</Button>
          </div>
          {canEdit && (
            <UndoRedoButtons
              canUndo={gridUndo.canUndo}
              canRedo={gridUndo.canRedo}
              busy={urBusy}
              onUndo={() => runGridHistory("undo")}
              onRedo={() => runGridHistory("redo")}
              testidPrefix="roster"
            />
          )}
          <CoverageGuardBadge
            verdict={cgVerdict}
            onOpenSettings={() => setCgDrawerOpen(true)}
            testid="coverage-guard-badge-roster"
            emptyHint="No Coverage Guard rules for this week. Click Manage Coverage Guard to set up."
            // Phase 2Z.aa: hide CG badge entirely for paid Starter.
            hidden={starterTrim}
          />
          {/* V1: Engine preview hidden — super-admin only diagnostic. */}
          {user?.role === "super_admin" && (
            <Link to={`/app/automation#rota_day`}
              data-testid="roster-engine-preview-link"
              className="inline-flex items-center text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[#1D4ED8] hover:bg-[#F6F9FE]">
              🔍 Engine preview
            </Link>
          )}
          {/* TP-3 -- Team Admin partial publish button.
              TP-MultiTeamTA (Feb 2026) -- wording flexes by team
              count: single-team keeps "Publish my team"; multi-team
              shows "Publish a team" so the user understands they
              will be asked which team. */}
          {canPublishTeam && (
            <Button
              variant="default"
              size="sm"
              onClick={publishMyTeam}
              disabled={publishingTeam || teamPublishedHere}
              data-testid="publish-my-team-btn"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {teamPublishedHere ? (
                <>
                  <Lock className="h-4 w-4 mr-1.5"/>
                  {myTeamIds.length > 1 ? "All my teams published" : "My team published"}
                </>
              ) : publishingTeam ? (
                <>Publishing…</>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1.5"/>
                  {myTeamIds.length > 1 ? "Publish a team" : "Publish my team"}
                </>
              )}
            </Button>
          )}
          {/* TP-AT.RS.8.6 (Feb 2026) -- quick actions extracted from
              the Actions dropdown so managers can reach the two
              most-used build tools with one click. Hidden below `lg`
              breakpoint (they stay inside the Week actions menu on
              narrow viewports so the header doesn't wrap into a
              chaotic stack). Permission gates are unchanged --
              `canRosterWrite` already guards the same handlers
              inside the dropdown. */}
          {canRosterWrite && (
            <span className="hidden lg:contents" data-testid="roster-quick-bulk-shift-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={()=>setBulkOpen(true)}
                data-testid="roster-quick-bulk-shift-btn"
                className="rounded-lg border-slate-200 bg-white hover:bg-[#F6F9FE] text-[#0B1A36]"
              >
                <Copy className="h-4 w-4 mr-1.5"/>Bulk apply
              </Button>
            </span>
          )}
          {canRosterWrite && (
            <span className="hidden lg:contents" data-testid="roster-quick-copy-prev-week-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={copyPrevWeek}
                data-testid="roster-quick-copy-prev-week-btn"
                className="rounded-lg border-slate-200 bg-white hover:bg-[#F6F9FE] text-[#0B1A36]"
              >
                <Copy className="h-4 w-4 mr-1.5"/>Copy previous week
              </Button>
            </span>
          )}
          {canEditByRole && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="roster-actions-btn"
                  className="rounded-full border border-[#DBE7FE] bg-[#F0F5FF] text-[#1D4ED8] hover:bg-[#DBE7FE] hover:text-[#1D4ED8] px-3.5 shadow-sm"
                >
                  <Layers className="h-4 w-4 mr-1.5"/>
                  Week actions
                  <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {/* Build group */}
                {(canRosterWrite || canUsePatternPicker || canRosterTemplates) && (
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                    Build roster
                  </DropdownMenuLabel>
                )}
                {canRosterWrite && (
                  <DropdownMenuItem onClick={()=>setBulkOpen(true)} data-testid="bulk-shift-btn" className="lg:hidden"><Copy className="h-4 w-4 mr-2"/>Bulk apply shift to employee…</DropdownMenuItem>
                )}
                {canRosterWrite && (
                  <DropdownMenuItem onClick={copyPrevWeek} data-testid="copy-prev-week-btn" className="lg:hidden"><Copy className="h-4 w-4 mr-2"/>Copy from previous week</DropdownMenuItem>
                )}
                {canUsePatternPicker && (
                  <DropdownMenuItem
                    onClick={() => {
                      // TP-RS.team.2 Phase 3 -- when the direct-apply
                      // flag is OFF for the tenant AND the caller is
                      // Team Admin, the menu item is interactive but
                      // surfaces a clear "not enabled yet" toast.
                      // Team Admin is never routed back into the
                      // ghost-draft path.
                      if (!directApplyEnabled && isTeamAdmin(user)) {
                        toast.error(
                          "Use a Rota Style pattern is not enabled for your team yet. Ask a Workspace Admin to enable Rota Style Apply.",
                          { duration: 6000 });
                        return;
                      }
                      openRotaStylePatternFlow();
                    }}
                    disabled={generatingPattern || patternScopeLoading}
                    data-testid="use-rota-style-pattern-btn"
                  >
                    <Repeat className="h-4 w-4 mr-2"/>
                    {generatingPattern ? "Generating…" : "Use a Rota Style pattern"}
                  </DropdownMenuItem>
                )}
                {canRosterTemplates && (
                  <DropdownMenuItem onClick={()=>setTplDialog({ open: true, mode: "save" })} data-testid="save-tpl-btn"><FileText className="h-4 w-4 mr-2"/>Save week as template…</DropdownMenuItem>
                )}
                {canRosterTemplates && templates.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="apply-tpl-sub"><FileText className="h-4 w-4 mr-2"/>Apply template</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {templates.map(t => (
                        <DropdownMenuItem key={t.template_id} onClick={()=>applyTemplate(t.template_id)} data-testid={`apply-tpl-${t.template_id}`}>{t.name}</DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                {/* Share group -- lock / reopen actions live here.
                    Publish itself is now a visible primary CTA
                    (Publish roster) when the WA can publish; on
                    narrow (<lg) viewports where the primary CTA
                    still shows, this section only carries lock /
                    reopen. */}
                {isCompanyAdmin && (status === "published" || status === "locked") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                      Share
                    </DropdownMenuLabel>
                    {status === "published" && (
                      <>
                        <DropdownMenuItem onClick={()=>setStatus("locked")} data-testid="status-lock"><Lock className="h-4 w-4 mr-2"/>Lock</DropdownMenuItem>
                        <DropdownMenuItem onClick={()=>setStatus("draft")}><Unlock className="h-4 w-4 mr-2"/>Move back to draft</DropdownMenuItem>
                      </>
                    )}
                    {status === "locked" && (
                      <DropdownMenuItem onClick={()=>setStatus("draft")} data-testid="status-reopen"><Unlock className="h-4 w-4 mr-2"/>Reopen for editing</DropdownMenuItem>
                    )}
                  </>
                )}
                {/* Export group */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  Export
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={exportXlsx} data-testid="roster-export-btn"><Download className="h-4 w-4 mr-2"/>Export to Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={exportPdf} data-testid="roster-export-pdf-btn"><FileText className="h-4 w-4 mr-2"/>Export to PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* TP-AT.RS.8.6 -- Publish roster visible primary CTA for
              Workspace Admin. Reuses `openPublishFlow` (same handler
              the dropdown item calls). Gated on `canPublishNow` so
              it only appears when the week actually needs a publish
              (draft OR published-with-new-draft-work). No new
              permission checks. */}
          {canPublishNow && (
            <Button
              variant="default"
              size="sm"
              onClick={openPublishFlow}
              data-testid="publish-roster-btn"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              <Send className="h-4 w-4 mr-1.5"/>
              Publish roster
            </Button>
          )}
        </div>
      </div>

      {/* TP-1 -- Team Admin scope banner. Sets expectations that the
          manager can view and (in later phases) edit/publish their
          own team only. Backend already scopes /roster to their
          team via _team_admin_scope; this banner just makes the
          UI mental model explicit.
          TP-MultiTeamTA (Feb 2026) -- copy flips to plural for a
          multi-team admin (assigned_team_ids.length >= 1). */}
      {user?.role === "team_admin" && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
          data-testid="roster-team-admin-banner"
        >
          <Info className="h-4 w-4 mt-0.5 text-blue-700 flex-shrink-0" />
          <div className="text-xs text-blue-900">
            {myTeamIds.length > 1 ? (
              <>
                <strong>Multi-team view.</strong> You manage{" "}
                {myTeamIds
                  .map((tid) => teamNameById?.[tid] || "Team")
                  .join(" and ")}
                . Other teams stay private to their own Team Admins and your Workspace Admin.
              </>
            ) : (
              <>
                <strong>Team view.</strong> You can view and edit your team only. Other teams stay private to their own Team Admins and your Workspace Admin.
              </>
            )}
          </div>
        </div>
      )}

      {/* TP-3 -- Workspace Admin partial-publish overview banner.
          Lists which teams have already been team-published (green)
          AND which teams have draft activity in this week (amber).
          Both states derive from existing data so no new backend
          tracking is needed. */}
      {isCompanyAdmin && (publishedTeamIds.length > 0 || draftTeamIds.length > 0) && (
        <div className="mb-4 space-y-2" data-testid="roster-team-state-summary">
          {publishedTeamIds.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
              data-testid="roster-partial-publish-banner"
            >
              <Lock className="h-4 w-4 mt-0.5 text-emerald-700 flex-shrink-0" />
              <div className="text-xs text-emerald-900">
                <strong>Published by team:</strong> {publishedTeamIds
                  .map(tid => teamNameById[tid] || "Team")
                  .join(", ")
                }. These cells are protected from accidental overwrite.
              </div>
            </div>
          )}
          {draftTeamIds.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
              data-testid="roster-team-draft-banner"
            >
              <Info className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
              <div className="text-xs text-amber-900">
                <strong>Team draft in progress:</strong> {draftTeamIds
                  .map(tid => teamNameById[tid] || "Team")
                  .join(", ")
                }. Not yet published.
              </div>
            </div>
          )}
        </div>
      )}

      <AdvisoryRowRota onDate={iso(weekStart)} />

      {/* TP-RS.team.2 Phase 3 -- Team Admin never lands on the
          Drafts route (`/app/drafts/rota/:period_id` is gated to
          Workspace Admin) so the nudge is suppressed to avoid a
          dead CTA. Workspace Admin still sees the nudge when any
          legacy drafts are pending. */}
      {!isTeamAdmin(user) && (
        <RosterDraftNudge weekDays={days.map((d) => iso(d))} refreshKey={draftNudgeRefreshKey} />
      )}

      {isLocked && (
        <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-200 text-sm text-[#4B5773] flex items-center gap-2">
          <Lock className="h-4 w-4"/>This week is locked. Reopen it from the Actions menu before editing.
        </div>
      )}

      {/* Team filter - visible to company_admin (and super_admin); team_admin sees a static badge */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {isCompanyAdmin ? (
          <>
            <Label className="eyebrow">Team filter</Label>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger data-testid="roster-team-filter" className="h-9 w-56 rounded-md border-slate-200 bg-white"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {teamFilter !== "all" && (
              <button onClick={()=>setTeamFilter("all")} data-testid="clear-team-filter"
                className="text-xs text-[#2563EB] hover:underline">Clear filter</button>
            )}
          </>
        ) : user.role === "team_admin" ? (
          <span className="text-xs px-3 py-1.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]" data-testid="team-admin-scope-badge">
            {myTeamIds.length > 1
              ? `Showing your ${myTeamIds.length} assigned teams`
              : "Showing your team only"}
          </span>
        ) : null}
      </div>

      <Legend />

      <RosterSummaryStrip
        employees={data.employees}
        shifts={data.shifts}
        allocations={data.allocations}
        conflicts={data.conflicts}
        bankHolidaysByEmp={data.bank_holidays}
        weekDays={days}
      />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden relative">
        {/* MOBILE-ONLY scroll hint: a vertical "More days" tab + stronger gradient
            on the right edge. Auto-hides the first time the user scrolls the
            grid horizontally - `scrollLeft > 12` is enough signal that they
            understand the gesture, so we don't keep nagging. */}
        {!hasScrolled && (
          <div className="pointer-events-none absolute inset-y-0 right-0 sm:hidden z-[6] flex items-stretch"
               data-testid="roster-scroll-hint" aria-hidden="true">
            <div className="w-12 bg-gradient-to-l from-white to-transparent"/>
            <div className="bg-white/95 border-l border-slate-200 px-1.5 flex flex-col items-center justify-center gap-1
                            animate-pulse shadow-[-4px_0_8px_-4px_rgba(31,42,68,0.18)]">
              <ChevronRight className="h-5 w-5 text-[#2563EB]"/>
              <div className="text-[9px] uppercase tracking-widest text-[#2563EB] font-semibold leading-none"
                   style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                Swipe for more days
              </div>
              <ChevronRight className="h-5 w-5 text-[#2563EB]"/>
            </div>
          </div>
        )}
        <div className="overflow-x-auto" ref={rosterScrollRef}
             onScroll={(e) => {
               if (!hasScrolled && e.currentTarget.scrollLeft > 12) setHasScrolled(true);
             }}>
          <table className="min-w-full text-sm" data-testid="roster-grid">
            <thead className="bg-[#EEF3FB]/60">
              <tr>
                <th className="text-left px-2 sm:px-4 py-3 sticky left-0 bg-[#EEF3FB]/95 backdrop-blur z-10 eyebrow font-normal w-[28vw] sm:w-auto min-w-[110px] sm:min-w-[220px] max-w-[60vw] shadow-[2px_0_0_0_rgba(0,0,0,0.04)]">Employee</th>
                {days.map((d, i) => {
                  // Visual-only header treatment — no data logic changes.
                  // today (matches client local date), weekend (Sat/Sun)
                  // and bank-holiday dates (any employee's BH set on this
                  // day) get subtle accents. The underlying day-key for
                  // each <th> stays identical.
                  const isWeekend = i === 5 || i === 6;
                  const todayIso = (() => {
                    const t = new Date();
                    return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,"0")}-${String(t.getDate()).padStart(2,"0")}`;
                  })();
                  const dIso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                  const isToday = dIso === todayIso;
                  const isBHDay = Object.values(data.bank_holidays || {}).some((arr) => Array.isArray(arr) && arr.includes(dIso));
                  return (
                    <th
                      key={i}
                      data-testid={`day-header-${dIso}`}
                      data-today={isToday ? "true" : undefined}
                      data-weekend={isWeekend ? "true" : undefined}
                      data-bank-holiday={isBHDay ? "true" : undefined}
                      className={`text-left px-2 sm:px-3 py-3 eyebrow font-normal min-w-[120px] sm:min-w-[170px] ${
                        isToday ? "bg-[#DBE7FE]/40 border-b-2 border-[#2563EB]" : isWeekend ? "bg-slate-50/60" : ""
                      }`}
                    >
                      <span className={isToday ? "text-[#1D4ED8] font-semibold" : ""}>
                        {DAY_LABELS[i]}
                      </span>
                      <span className="text-[#4B5773] normal-case tracking-normal"> · {d.getDate()}/{d.getMonth()+1}</span>
                      {isBHDay && (
                        <span className="inline-block ml-1.5 w-1.5 h-1.5 rounded-full bg-sky-400 align-middle" title="Bank holiday" data-testid={`day-header-bh-dot-${dIso}`} />
                      )}
                      {isToday && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#1D4ED8] font-semibold" data-testid={`day-header-today-${dIso}`}>Today</span>
                      )}
                    </th>
                  );
                })}
                <th className="text-right px-4 py-3 eyebrow font-normal hidden sm:table-cell">Week</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rosterRows.map((row, idx) => {
                if (row.kind === "team_header") {
                  // TP-MultiTeam.grouping (Feb 2026) -- visual section
                  // header. Keeps the existing skip-cell behaviour and
                  // does not introduce a new interactive surface.
                  return (
                    <tr
                      key={`th-${row.team_id}`}
                      data-testid={`roster-team-header-${row.team_id}`}
                      className="bg-[#F4F8FF]"
                    >
                      <td colSpan={9}
                          className="px-2 sm:px-4 py-2 text-[11px] uppercase tracking-widest font-semibold text-[#1D4ED8]">
                        {row.team_name}
                        <span className="ml-2 text-[10px] text-[#94A3B8] normal-case tracking-normal font-medium">
                          {row.count} {row.count === 1 ? "person" : "people"}
                        </span>
                      </td>
                    </tr>
                  );
                }
                const emp = row.emp;
                const allocM = totalForEmployee(emp.employee_id);
                const capM = capacityForEmployee(emp.employee_id);
                // TP-3.2 -- subtle team-state row background.
                // Shown only to Workspace/Company Admin. Green when
                // the employee's team has been partial-published;
                // amber when their team has draft activity on this
                // week but is not yet published. Hover state remains
                // unchanged so cell-level interactions stay normal.
                const rowTeamClass = (isCompanyAdmin
                  ? (publishedTeamIds.includes(emp.team_id)
                      ? "bg-emerald-50/40 hover:bg-emerald-50/60"
                      : (draftTeamIds.includes(emp.team_id)
                          ? "bg-amber-50/40 hover:bg-amber-50/60"
                          : "hover:bg-[#EEF3FB]/40"))
                  : "hover:bg-[#EEF3FB]/40");
                const rowTeamLabel = isCompanyAdmin
                  ? (publishedTeamIds.includes(emp.team_id)
                      ? "Published by team"
                      : (draftTeamIds.includes(emp.team_id)
                          ? "Team draft"
                          : null))
                  : null;
                return (
                  <tr key={emp.employee_id}
                      className={rowTeamClass}
                      data-team-state={rowTeamLabel || "none"}
                      data-testid={`roster-row-${emp.employee_id}`}>
                    <td className="px-2 sm:px-4 py-3 sticky left-0 bg-white z-10 w-[28vw] sm:w-auto max-w-[60vw] shadow-[2px_0_0_0_rgba(0,0,0,0.04)]">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {/* Avatar is hidden on mobile to maximise day-column width. */}
                        <div className="hidden sm:block">
                          <div className="rounded-full ring-2 ring-white shadow-[0_0_0_1px_rgba(11,26,54,0.08)]">
                            <Avatar name={emp.name} url={emp.avatar_url} size={32}/>
                          </div>
                        </div>
                        <div className="min-w-0">
                          {/* Mobile: "First L." (compact). Desktop: full name. */}
                          <div className="font-medium text-sm sm:text-base truncate sm:hidden" data-testid={`emp-name-compact-${emp.employee_id}`}>
                            {(() => {
                              const parts = (emp.name || "").trim().split(/\s+/);
                              if (parts.length === 1) return parts[0];
                              return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                            })()}
                          </div>
                          <div className="font-semibold text-base truncate hidden sm:block text-[#0B1A36]">{emp.name}</div>
                          <div className="text-xs text-[#4B5773] hidden sm:block">{emp.country}</div>
                          {/* Phase 2Z.ae - tiny skills hint. Desktop only
                              so the mobile compact name layout stays
                              identical. Capped + overflow handled by the
                              shared component. Returns nothing when the
                              employee has no skill_ids set. */}
                          <div className="hidden sm:block">
                            <EmployeeSkillsLine
                              skillIds={emp.skill_ids}
                              skillNameById={skillNameById}
                              testidPrefix={`emp-${emp.employee_id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    {days.map((d, i) => {
                      const key = `${emp.employee_id}_${iso(d)}`;
                      const s = shiftMap[key]; const a = allocMap[key];
                      const conflict = data.conflicts[key] || {};
                      const isBH = conflict.is_bank_holiday;
                      // Feb 2026 - lookup the configured holiday name so
                      // the pill can render "Christmas Day" rather than
                      // the generic "Bank Holiday". Falls back to null
                      // (pill uses default copy) when no name is stored.
                      const bhName = (data.bank_holiday_names?.[emp.employee_id] || {})[iso(d)] || null;
                      return (
                        <td key={i} className="px-2 py-2 align-top">
                          <button onClick={()=>openCell(emp, d)} data-testid={`cell-${emp.employee_id}-${iso(d)}`}
                            className="w-full text-left">
                            <CellContent shift={s} alloc={a} conflict={conflict} isBH={isBH} bhName={bhName} categories={categories} canEdit={canEdit}
                                          day={iso(d)} showLocations={true} locations={locations}/>
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right hidden sm:table-cell">
                      <div className="font-display text-lg">{(allocM/60).toFixed(1)}<span className="text-xs text-[#4B5773] ml-1">h</span></div>
                      <div className="text-[10px] text-[#4B5773]">of {(capM/60).toFixed(1)}h</div>
                    </td>
                  </tr>
                );
              })}
              {!data.employees.length && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#4B5773]">No employees to schedule yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cell editor */}
      <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); else setOpen(true); }}>
        <DialogContent className="max-w-2xl" fullscreenOnMobile>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {edit?.employee?.name} <span className="text-[#4B5773] text-base font-normal">· {edit?.work_date}</span>
              {edit?.is_bh && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Bank Holiday</span>}
            </DialogTitle>
          </DialogHeader>
          {edit && (
            <form onSubmit={saveAll}>
              {canEdit && shiftTemplates.length > 0 && (
                <div className="rounded-lg bg-[#F0F5FF] border border-[#DBE7FE] p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="shift-tpl-picker">
                  <span className="text-xs text-[#1D4ED8] font-medium">Apply template</span>
                  <Select value="" onValueChange={(id) => {
                    const tpl = shiftTemplates.find(t => t.shift_template_id === id);
                    if (!tpl) return;
                    // TP-AT.RS.8.1 (Feb 2026) -- preserve each
                    // template entry's start_time / end_time so
                    // Break windows land on the correct wall clock
                    // after apply (default templates rely on this).
                    const tplEntries = (tpl.entries || []).map(e => ({
                      category_id: e.category_id || "",
                      combo_id: e.combo_id || null,
                      combo_label: e.combo_id
                        ? (combos.find(c => c.combo_id === e.combo_id)?.name || null)
                        : null,
                      minutes: Number(e.minutes),
                      start_time: e.start_time || null,
                      end_time: e.end_time || null,
                      note: e.note || null,
                    }));
                    // TP-AT.RS.8.1 -- derive blank Work gap rows.
                    // Default templates store Break entries only;
                    // Work rows stay blank so the manager picks
                    // the correct activity for their business. The
                    // helper computes any uncovered stretch of the
                    // shift envelope and inserts blank rows with
                    // `_wanted_kind: "work"` so the row picker
                    // filters to Work categories on the editor.
                    // Existing custom templates keep working:
                    // rows with a category_id fall through
                    // unchanged; only genuine envelope gaps get
                    // filled with a blank Work row.
                    const derived = deriveBlankWorkGapEntries(
                      tpl.start_time, tpl.end_time, tplEntries);
                    // Preserve any leave-sourced entries already on the cell.
                    const preserved = edit.entries.filter(e => e.source_leave_id);
                    setEdit({
                      ...edit,
                      start_time: tpl.start_time, end_time: tpl.end_time,
                      break_minutes: tpl.break_minutes, break_start_time: tpl.break_start_time || "",
                      notes: tpl.notes || edit.notes,
                      entries: [...preserved, ...derived],
                    });
                    toast.success(`Loaded "${tpl.name}"`);
                  }}>
                    <SelectTrigger className="h-8 w-56 rounded-md bg-white text-xs">
                      <SelectValue placeholder="Pick a saved template…"/>
                    </SelectTrigger>
                    <SelectContent>
                      {shiftTemplates.map(t => (
                        <SelectItem key={t.shift_template_id} value={t.shift_template_id} data-testid={`shift-tpl-pick-${t.shift_template_id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: t.color || "#2563EB" }}/>
                            {t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* TP-AT.RS.7 (Phase 1) -- legacy break_minutes and
                  break_start_time inputs are hidden. Break is now a
                  visible Away block inside the Assignments section.
                  The shift envelope (Start / End) stays as a two-col
                  grid; Break is managed inline via the Break row and
                  the "Add break" button below the Assignments list. */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <Field label="Start"><Input type="time" step="1800" required value={edit.start_time||""} data-testid="shift-start-input"
                  onChange={(e)=>setEdit({...edit, start_time: e.target.value})} className="h-11 rounded-lg"/></Field>
                <Field label="End"><Input type="time" step="1800" required value={edit.end_time||""} data-testid="shift-end-input"
                  onChange={(e)=>setEdit({...edit, end_time: e.target.value})} className="h-11 rounded-lg"/></Field>
              </div>

              {/* Optional shift location (advisory only, never enforced) */}
              {locations.length > 0 && (
                <div className="mb-4" data-testid="shift-location-row">
                  <Label className="eyebrow">Location (optional)</Label>
                  <Select
                    value={edit.location_id || "none"}
                    onValueChange={(v) =>
                      setEdit({ ...edit, location_id: v === "none" ? "" : v })
                    }>
                    <SelectTrigger className="h-11 rounded-lg mt-1"
                                    data-testid="shift-location-select">
                      <SelectValue placeholder="- No location -" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">- No location -</SelectItem>
                      {locations.map((l) => (
                        <SelectItem key={l.location_id} value={l.location_id}
                                     data-testid={`shift-location-opt-${l.location_id}`}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full"
                                   style={{ background: l.colour || "#64748B" }} />
                            {l.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="eyebrow">Assignments</div>
                  <div className="text-xs">
                    <span className={`${editAllocated > editCapacity ? "text-red-600 font-medium" : editAllocated < editCapacity ? "text-amber-700" : "text-emerald-700 font-medium"}`}>
                      {minsToHM(editAllocated)} of {minsToHM(editCapacity)} allocated
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {edit.entries.map((entry, idx) => {
                    const cat = categories.find(c => c.category_id === entry.category_id)
                      || allCategories.find(c => c.category_id === entry.category_id);
                    const combo = entry.combo_id ? combos.find(c => c.combo_id === entry.combo_id) : null;
                    const isLeave = !!entry.source_leave_id;
                    // TP-AT.RS.7 (Phase 1) -- Away (Break) entries
                    // render with a locked category label, a purple
                    // dot, and no combo / category dropdown. Time
                    // pickers stay editable so the manager can move
                    // or resize the break window; the X button is
                    // still available to remove it. Away rows are
                    // never marked "invalid" -- they always have a
                    // valid category from the seeded Break row.
                    const isAway = !isLeave && awayCategoryIds.has(entry.category_id);
                    const minsNum = Number(entry.minutes);
                    const hasPicker = !!entry.category_id || !!entry.combo_id;
                    const isInvalid = !isLeave && !isAway && (!hasPicker || !(minsNum > 0));
                    // Picker value uses prefix so categories and combos can coexist.
                    const pickerValue = combo ? `combo:${combo.combo_id}` : (entry.category_id ? `cat:${entry.category_id}` : "");
                    const onPickerChange = (v) => {
                      if (v.startsWith("combo:")) {
                        const cid = v.slice(6);
                        const c = combos.find(x => x.combo_id === cid);
                        updateEntries(idx, { combo_id: cid, combo_label: c?.name || null, category_id: "" });
                      } else if (v.startsWith("cat:")) {
                        updateEntries(idx, { category_id: v.slice(4), combo_id: null, combo_label: null });
                      }
                    };
                    return (
                      <div key={idx} data-testid={`alloc-row-${idx}`}
                        data-row-kind={isAway ? "away" : (isLeave ? "leave" : "work")}
                        className={`rounded-lg border p-2 ${isInvalid ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-white"}`}>
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full ml-1" style={{ backgroundColor: (combo?.color) || cat?.color || "#999" }} />
                          {isLeave ? (
                            <div className="flex-1 px-2 py-1 text-sm">
                              <span className="font-medium">{entry.category_name}</span>{" "}
                              <span className="text-xs text-[#4B5773]">· from leave request</span>
                            </div>
                          ) : (
                            <Select value={pickerValue} onValueChange={onPickerChange}>
                              <SelectTrigger data-testid={`alloc-cat-${idx}`} className="flex-1 h-9 rounded-md border-slate-200"><SelectValue placeholder={entry._wanted_kind === "work" ? "Select work activity" : (entry._wanted_kind === "away" ? "Select away activity" : "Select activity")}/></SelectTrigger>
                              <SelectContent>
                                {/* TP-AT.RS.8 -- when the row was
                                    created by the "+ Add work" or
                                    "+ Add away" quick button we
                                    filter the picker to that kind
                                    only so managers see the
                                    relevant activities. Rows
                                    without a hint (legacy save/
                                    reopen path) still show every
                                    Work + Away category. Combos
                                    stay Work-only. */}
                                {categories
                                  .filter(c => {
                                    const wk = entry._wanted_kind;
                                    if (wk === "work") return c.kind === "work";
                                    if (wk === "away") return c.kind === "away";
                                    return true;
                                  })
                                  .map(c => (
                                  <SelectItem key={c.category_id} value={`cat:${c.category_id}`}>
                                    {c.kind === "away" ? (
                                      <span className="inline-flex items-center gap-2">
                                        {c.name}
                                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE9FE] text-[#5B21B6]">Away</span>
                                      </span>
                                    ) : c.name}
                                  </SelectItem>
                                ))}
                                {combos.length > 0 && entry._wanted_kind !== "away" && (
                                  <>
                                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#4B5773]">Combos</div>
                                    {combos.map(c => (
                                      <SelectItem key={c.combo_id} value={`combo:${c.combo_id}`}>
                                        <span className="inline-flex items-center gap-2">
                                          {c.name}
                                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]">Combo</span>
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                          <Input type="time"
                            value={entry.start_time || ""}
                            disabled={isLeave}
                            data-testid={`alloc-start-${idx}`}
                            onChange={(e)=>setEntryTime(idx, "start_time", e.target.value)}
                            className="h-9 w-28 rounded-md"/>
                          <span className="text-xs text-[#4B5773]">-</span>
                          <Input type="time"
                            value={entry.end_time || ""}
                            disabled={isLeave}
                            data-testid={`alloc-end-${idx}`}
                            onChange={(e)=>setEntryTime(idx, "end_time", e.target.value)}
                            className="h-9 w-28 rounded-md"/>
                          <span
                            data-testid={`alloc-duration-${idx}`}
                            className={`text-xs px-2 py-1 rounded-full ${minsNum > 0 ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-400"}`}>
                            {(() => {
                              // TP-AT chip rule. When both clock
                              // times are set and the pair is
                              // invalid (end <= start), show a
                              // dash. Otherwise show the duration
                              // computed from the times (break
                              // overlap is subtracted only when an
                              // explicit break_start_time is set).
                              // TP-AT.RS.7 (Phase 1) -- Away entries
                              // (the Break block itself) must show
                              // wall time only: the helper would
                              // otherwise subtract its own overlap
                              // and render 0m.
                              if (entry.start_time && entry.end_time) {
                                if (isAway) {
                                  const s = entry.start_time.split(":");
                                  const e = entry.end_time.split(":");
                                  const m = (Number(e[0])*60 + Number(e[1]))
                                          - (Number(s[0])*60 + Number(s[1]));
                                  if (m <= 0) return "-";
                                  return formatHM(m);
                                }
                                const m = entryMinutesFromTimes({
                                  start: entry.start_time,
                                  end: entry.end_time,
                                  shiftStart: edit.start_time,
                                  shiftEnd: edit.end_time,
                                  breakMinutes: Number(edit.break_minutes || 0),
                                  breakStart: edit.break_start_time,
                                });
                                if (m == null) return "-";
                                if (m === 0) return "0m";
                                return formatHM(m);
                              }
                              return minsNum > 0 ? formatHM(minsNum) : "-";
                            })()}
                          </span>
                          {showAdvancedMinutes && (
                            <>
                              <Input type="number" step="30" min="0"
                                value={entry.minutes} disabled={isLeave}
                                data-testid={`alloc-min-${idx}`}
                                onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); } }}
                                onChange={(e)=>updateEntry(idx, "minutes", e.target.value)}
                                className="h-9 w-20 rounded-md"/>
                              <span className="text-xs text-[#4B5773]">min</span>
                            </>
                          )}
                          {!isLeave && (
                            <Button type="button" variant="ghost" size="sm" onClick={()=>removeEntry(idx)} data-testid={`alloc-remove-${idx}`}><X className="h-4 w-4"/></Button>
                          )}
                        </div>
                        {!isLeave && (
                          <Input
                            value={entry.note || ""}
                            data-testid={`alloc-note-${idx}`}
                            placeholder="Note for this assignment (visible to the employee)"
                            onChange={(e)=>updateEntry(idx, "note", e.target.value)}
                            className="mt-2 h-8 text-xs rounded-md bg-slate-50 border-slate-200 placeholder:text-slate-400"
                          />
                        )}
                        {/* Combo breakdown preview - shows admin where the minutes will go */}
                        {combo && minsNum > 0 && (
                          <div className="mt-2 text-[11px] text-[#4B5773] flex flex-wrap items-center gap-1.5" data-testid={`alloc-combo-breakdown-${idx}`}>
                            <span className="uppercase tracking-widest text-[10px] text-[#1D4ED8] font-medium">Splits as</span>
                            {combo.components.map((cmp) => {
                              const cName = categories.find(x => x.category_id === cmp.category_id)?.name || "-";
                              const min = Math.round((minsNum * Number(cmp.percentage)) / 100);
                              return <span key={cmp.category_id} className="px-1.5 py-0.5 rounded-full bg-white border border-slate-200">{cmp.percentage}% {cName} · {min} min</span>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* TP-AT.RS.8 -- clearer discoverability for
                      managers. The previous single "+ Add
                      assignment" button relied on managers
                      knowing that Break lives in the same
                      dropdown as Work categories, which caused
                      confusion for new customers. We now show
                      two quick buttons - "+ Add work" and "+ Add
                      away" - that both call the same addEntry
                      chronological fill. Both create normal
                      assignment rows in the existing model:
                      no new data type, no compliance engine, no
                      break rules. "+ Add work" leaves the
                      category blank so the manager picks the
                      correct Work activity for their business.
                      "+ Add away" pre-selects the seeded Break
                      Away category when it exists on the tenant
                      so the row is saveable immediately. */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline"
                      onClick={() => addEntry("work")}
                      data-testid="alloc-add-work-btn"
                      className="h-9 rounded-md border-dashed">
                      <Plus className="h-4 w-4 mr-1"/>Add work
                    </Button>
                    <Button type="button" variant="outline"
                      onClick={() => addEntry("away")}
                      data-testid="alloc-add-away-btn"
                      className="h-9 rounded-md border-dashed">
                      <Plus className="h-4 w-4 mr-1"/>Add break / away
                    </Button>
                  </div>
                  {/* TP-AT.RS.7.2 -- soft-refuse hint shown only when
                      the manager clicks Add on a fully-scheduled
                      shift. The button stays enabled; no row is
                      created; the manager is invited to adjust an
                      existing row first. */}
                  {noTimeLeftMsg && (
                    <div data-testid="alloc-no-time-left" className="mt-2 text-xs text-amber-700">
                      No unassigned time left. Adjust an existing row first.
                    </div>
                  )}
                </div>
                {/* TP-AT.RS.7.1 -- footer summary. Wording switched
                    from "Allocated" to "Scheduled" so Away time is
                    not implied to be work. */}
                {(() => {
                  const scheduled = editAllocated;
                  const unassigned = Math.max(0, editCapacity - scheduled);
                  const over = scheduled > editCapacity;
                  const overlaps = findOverlaps(edit.entries);
                  const overlapMsg = overlaps.length > 0
                    ? `Overlap detected between ${overlaps.length === 1 ? "two assignments" : `${overlaps.length} pairs`}`
                    : null;
                  return (
                    <div className="mt-3 border-t border-slate-200 pt-2 text-xs">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span data-testid="alloc-summary-scheduled" className={over ? "text-red-700 font-medium" : "text-slate-700"}>
                          Scheduled <span className="font-medium">{formatHM(scheduled)}</span> of {formatHM(editCapacity)}
                        </span>
                        <span data-testid="alloc-summary-work" className="text-slate-700">
                          Work <span className="font-medium">{formatHM(editWork)}</span>
                        </span>
                        <span data-testid="alloc-summary-away" className="text-[#5B21B6]">
                          Away <span className="font-medium">{formatHM(editAway)}</span>
                        </span>
                        <span data-testid="alloc-summary-unassigned"
                          className={unassigned > 0 ? "text-amber-700" : "text-emerald-700"}>
                          Unassigned <span className="font-medium">{formatHM(unassigned)}</span>
                        </span>
                        {overlapMsg && (
                          <span data-testid="alloc-overlap-warning" className="text-red-700 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5"/>{overlapMsg}
                          </span>
                        )}
                        <button type="button"
                          data-testid="alloc-toggle-advanced"
                          onClick={()=>setShowAdvancedMinutes(v=>!v)}
                          className="ml-auto text-[11px] text-[#4B5773] underline-offset-2 hover:underline">
                          {showAdvancedMinutes ? "Hide minutes" : "Advanced: show minutes"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {edit.conflict?.messages?.length > 0 && (
                <div className={`rounded-lg border p-3 mb-4 ${edit.conflict.status === "error" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                  <div className="flex items-center gap-2 text-xs font-medium">
                    {edit.conflict.status === "error" ? <AlertCircle className="h-4 w-4"/> : <AlertTriangle className="h-4 w-4"/>}
                    {edit.conflict.messages.join(" · ")}
                  </div>
                </div>
              )}

              {/* Timeline preview - assignment_start_times calculated from shift + break. */}
              <AssignmentTimeline shift={edit} categories={categories}/>

              <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
                {edit.hasShift ? (
                  <Button type="button" variant="ghost" onClick={clearShift} data-testid="shift-clear-btn" className="text-slate-600 hover:text-red-600">Clear day</Button>
                ) : <span />}
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" data-testid="shift-close-btn" onClick={requestClose}
                    className="rounded-lg border-slate-200">Close</Button>
                  <Button type="submit" data-testid="shift-save-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Save</Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {/* Bulk shift dialog */}
      <BulkShiftDialog open={bulkOpen} onOpenChange={setBulkOpen} weekStart={weekStart}
        employees={data.employees} teams={teams} userRole={user.role}
        shiftTemplates={shiftTemplates} onApplied={load} />

      {/* Save-as-template dialog */}
      {/* TP-RS.team.1 -- pattern scope picker. Opens when the
          Workspace Admin clicks Actions ▶ Use a Rota Style pattern
          AND at least one team-scoped profile exists. For legacy
          tenants (no team profiles) the menu item short-circuits
          and skips this dialog entirely so the flow is byte-stable. */}
      <Dialog
        open={patternScopeOpen}
        onOpenChange={(o)=> setPatternScopeOpen(o)}
      >
        <DialogContent data-testid="roster-pattern-scope-dialog">
          <DialogHeader>
            <DialogTitle>Which pattern do you want to apply?</DialogTitle>
            <DialogDescription>
              Pick the Rota Style pattern to apply to {iso(weekStart)}. The company-wide pattern only fills teams that do not have their own pattern; team patterns only affect their own employees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1" data-testid="roster-pattern-scope-options">
            {(() => {
              const _isTA = isTeamAdmin(user);
              const companyWide = patternProfiles.find((p) => !p.team_id) || null;
              const teamRowsSource = _isTA
                ? (teams || []).filter((t) => myTeamIds.includes(t.team_id))
                : (teams || []);
              const rows = [
                // TP-MultiTeamTA (Feb 2026) -- Team Admin never sees
                // company-wide. Workspace Admin keeps the row.
                ...(_isTA ? [] : [{
                  key: "company",
                  title: "Company-wide pattern",
                  subtitle: companyWide
                    ? `Weekly default · ${companyWide.cycle_type} · used for teams without their own pattern`
                    : "No company-wide Rota Style yet",
                  profileId: companyWide?.profile_id || null,
                  teamId: null,
                  disabled: !companyWide,
                }]),
                ...teamRowsSource.map((t) => {
                  const prof = patternProfiles.find((p) => p.team_id === t.team_id) || null;
                  return {
                    key: t.team_id,
                    title: `${t.name} pattern`,
                    subtitle: prof
                      ? `Team-scoped · ${prof.cycle_type} · only affects ${t.name} employees`
                      : `No Rota Style has been set up for ${t.name} yet`,
                    profileId: prof?.profile_id || null,
                    teamId: t.team_id,
                    disabled: !prof,
                  };
                }),
              ];
              return rows.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  disabled={row.disabled || generatingPattern}
                  onClick={() => {
                    setPatternScopeOpen(false);
                    applyRotaStylePattern(row.profileId,
                      row.teamId ? { teamId: row.teamId } : {});
                  }}
                  className={`w-full text-left rounded-lg border px-3.5 py-3 transition-colors ${
                    row.disabled
                      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-white border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/30 text-[#0B1A36]"
                  }`}
                  data-testid={`roster-pattern-scope-${row.key}`}
                >
                  <div className="text-[13px] font-semibold">{row.title}</div>
                  <div className="text-[11px] mt-0.5 leading-relaxed">
                    {row.subtitle}
                  </div>
                  {row.disabled && (
                    <div className="text-[11px] mt-1.5 text-amber-700 font-medium">
                      Open Rota Style to set this pattern up first.
                    </div>
                  )}
                </button>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPatternScopeOpen(false)}
              data-testid="roster-pattern-scope-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* TP-RS.team.2 Phase 3 -- Replace / Cancel dialog. Opens when
          the new direct-apply endpoint returns 409
          existing_visible_roster. Cancel closes the dialog and
          fires NO backend call; Replace re-runs the apply with
          replace=true. */}
      <Dialog
        open={replaceDialog.open}
        onOpenChange={(o) => {
          if (!o) setReplaceDialog((d) => ({ ...d, open: false }));
        }}
      >
        <DialogContent data-testid="roster-apply-replace-dialog">
          <DialogHeader>
            <DialogTitle>Replace this week&apos;s roster?</DialogTitle>
            <DialogDescription>
              {`This team already has a draft roster for this week. Replace it with the Rota Style pattern?`}
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-[#4B5773] space-y-1">
            <div>
              <span className="font-medium text-[#0B1A36]">Team:</span>{" "}
              {replaceDialog.teamName}
            </div>
            <div>
              <span className="font-medium text-[#0B1A36]">Week starting:</span>{" "}
              {replaceDialog.weekStart}
            </div>
            <div className="text-[#4B5773] pt-2">
              Replace removes the existing unpublished rows for this team and week only. Published rows are protected. Leave entries are preserved.
            </div>
            <div className="text-[#4B5773]">
              Apply is not Publish - nothing is sent to employees until you publish.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReplaceDialog((d) => ({ ...d, open: false }))}
              data-testid="roster-apply-replace-cancel"
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const pid = replaceDialog.profileId;
                const tid = replaceDialog.teamId;
                setReplaceDialog((d) => ({ ...d, open: false }));
                // TP-MultiTeamTA (Feb 2026) -- forward the team_id
                // captured at 409-time so a multi-team TA still
                // replaces into the exact team they originally
                // chose. WA paths leave teamId null which keeps
                // the legacy all-teams behaviour.
                await applyRotaStylePattern(pid, { replace: true, teamId: tid });
              }}
              data-testid="roster-apply-replace-confirm"
              className="rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* TP-RS.team.2 Phase 3 -- Validation error dialog. Opens
          whenever the backend reports `status: blocked, reason:
          invalid_pattern`. Lists every issue so the manager can
          fix the Rota Style profile before retrying. No writes
          happen while this dialog is open. */}
      <Dialog
        open={validationDialog.open}
        onOpenChange={(o) => {
          if (!o) setValidationDialog({ open: false, issues: [] });
        }}
      >
        <DialogContent data-testid="roster-apply-validation-dialog">
          <DialogHeader>
            <DialogTitle>This Rota Style pattern can&apos;t be applied yet</DialogTitle>
            <DialogDescription>
              Please fix the issues below in Rota Style, then try Apply again. Nothing was changed on the roster.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[260px] overflow-y-auto"
               data-testid="roster-apply-validation-issues">
            {(validationDialog.issues || []).length === 0 ? (
              <div className="text-xs text-[#4B5773]">
                The pattern is missing required information.
              </div>
            ) : (validationDialog.issues || []).map((issue, idx) => (
              <div
                key={idx}
                className="text-xs text-[#0B1A36] p-2 rounded border border-amber-200 bg-amber-50"
                data-testid={`roster-apply-validation-issue-${idx}`}
              >
                <div className="font-medium uppercase tracking-widest text-[10px] text-amber-900 mb-0.5">
                  {(issue?.rule || "issue").replace(/_/g, " ")}
                </div>
                <div>{issue?.message || "Pattern data is invalid."}</div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setValidationDialog({ open: false, issues: [] })}
              data-testid="roster-apply-validation-close"
              className="rounded-lg"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setValidationDialog({ open: false, issues: [] });
                navigate("/app/rota-style");
              }}
              data-testid="roster-apply-validation-open-rota-style"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              Open Rota Style
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={tplDialog.open} onOpenChange={(o)=>setTplDialog({...tplDialog, open: o})}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save current week as template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="eyebrow">Template name</Label>
            <Input value={tplName} onChange={(e)=>setTplName(e.target.value)} placeholder="e.g. Standard UK Week"
              data-testid="tpl-name-save-input" className="h-11 rounded-lg"/>
            <p className="text-xs text-[#4B5773]">Shifts and assignment allocations for {iso(weekStart)} will be snapshotted. Leave entries are not included.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveAsTemplate} disabled={!tplName.trim()} data-testid="tpl-save-confirm"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CoverageGuardDrawer
        open={cgDrawerOpen}
        onOpenChange={setCgDrawerOpen}
        categories={categories}
        teams={teams}
        rules={cgRules}
        onChanged={cgReload}
      />
      {/* TP-RS.team.1.2 - Workspace Admin publish-scope picker. */}
      <Dialog open={publishScopeOpen} onOpenChange={setPublishScopeOpen}>
        <DialogContent data-testid="roster-publish-scope-dialog">
          <DialogHeader>
            <DialogTitle>Publish this week</DialogTitle>
            <DialogDescription>
              {publishedTeamIds.length > 0
                ? "Some teams are already published. Published team cells stay protected. Choose what to publish next."
                : "Choose which teams to publish. Selecting one team behaves exactly like that team's Team Admin publishing their own team."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
              Publish scope
            </div>
            <div className="flex flex-wrap gap-1.5"
                 data-testid="roster-publish-scope-picker">
              <button type="button"
                onClick={() => setPublishScope("all")}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                  publishScope === "all"
                    ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                    : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                }`}
                data-testid="roster-publish-scope-all">
                Publish all teams
              </button>
              {Object.entries(teamNameById).map(([tid, tname]) => {
                const already = publishedTeamIds.includes(tid);
                return (
                  <button key={tid} type="button"
                    onClick={() => setPublishScope(tid)}
                    disabled={already}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                      publishScope === tid
                        ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                        : already
                          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                          : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`roster-publish-scope-team-${tid}`}
                    title={already ? "This team is already published." : ""}>
                    {tname} only
                    {already && (
                      <span className="text-[10px] text-emerald-600">· published</span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#4B5773] pt-1">
              {publishScope === "all"
                ? "Publishes every team. Already-published teams are protected and skipped."
                : `Publishes ${teamNameById?.[publishScope] || "this team"} only. Other teams stay as a draft. Same behaviour as Team Admin publishing.`}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => setPublishScopeOpen(false)}
                    data-testid="roster-publish-scope-cancel-btn">Cancel</Button>
            <Button onClick={confirmPublishScope}
                    disabled={publishingTeam}
                    data-testid="roster-publish-scope-confirm-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="h-4 w-4 mr-2" />
              {publishScope === "all"
                ? "Publish all teams"
                : `Publish ${teamNameById?.[publishScope] || "team"} only`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* TP-MultiTeamTA (Feb 2026) -- Team Admin multi-team publish
          picker. Only opens when a Team Admin with 2+ assigned teams
          clicks Publish. Explicit choice; no silent fan-out. */}
      <Dialog open={multiTeamPublishOpen}
              onOpenChange={(o) => {
                setMultiTeamPublishOpen(o);
                if (!o) setMultiTeamPublishPicked(null);
              }}>
        <DialogContent data-testid="ta-multi-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publish which team?</DialogTitle>
            <DialogDescription>
              You manage more than one team. Pick the team to publish for this week. Each team is published separately; already-published teams are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
              Your assigned teams
            </div>
            <div className="flex flex-wrap gap-1.5"
                 data-testid="ta-multi-publish-picker">
              {myTeamIds.map((tid) => {
                const tname = teamNameById?.[tid] || tid;
                const already = publishedTeamIds.includes(tid);
                const isActive = multiTeamPublishPicked === tid;
                return (
                  <button key={tid} type="button"
                    onClick={() => !already && setMultiTeamPublishPicked(tid)}
                    disabled={already}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                      isActive
                        ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                        : already
                          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                          : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`ta-multi-publish-team-${tid}`}
                    title={already ? "This team is already published." : ""}>
                    {tname}
                    {already && (
                      <span className="text-[10px] text-emerald-600">· published</span>
                    )}
                  </button>
                );
              })}
            </div>
            {multiTeamPublishPicked && (
              <p className="text-[11px] text-[#4B5773] pt-1"
                 data-testid="ta-multi-publish-summary">
                Publishes <strong>{teamNameById?.[multiTeamPublishPicked] || "this team"}</strong> only. Other assigned teams stay as a draft.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => setMultiTeamPublishOpen(false)}
                    data-testid="ta-multi-publish-cancel-btn">Cancel</Button>
            <Button
              onClick={async () => {
                const tid = multiTeamPublishPicked;
                if (!tid) return;
                setMultiTeamPublishOpen(false);
                await _publishOneTeam(tid);
                setMultiTeamPublishPicked(null);
              }}
              disabled={!multiTeamPublishPicked || publishingTeam}
              data-testid="ta-multi-publish-confirm-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="h-4 w-4 mr-2" />
              {multiTeamPublishPicked
                ? `Publish ${teamNameById?.[multiTeamPublishPicked] || "team"}`
                : "Pick a team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Undo/Redo V1 -- compare-and-warn confirmation. */}
      <UndoRedoConfirmDialog
        mode={urConfirm}
        onCancel={() => setUrConfirm(null)}
        onProceed={() => { const m = urConfirm; setUrConfirm(null); runGridHistory(m, true); }}
        testidPrefix="roster"
      />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { bg: "bg-slate-50", text: "text-slate-700", ring: "ring-slate-200", dot: "bg-slate-400", label: "Draft" },
    published: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500", label: "Published" },
    locked: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500", label: "Locked" },
    // TP-3.2 -- partial-publish display states. Backend never stores
    // these; they are computed by RosterPage from published_team_ids.
    partial: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500", label: "Partially published" },
    my_team_published: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500", label: "Published" },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase ring-1 ${s.bg} ${s.text} ${s.ring}`}
      data-testid="period-status"
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function CellContent({ shift, alloc, conflict, isBH, bhName, categories, canEdit,
                       day, showLocations = false, locations = [] }) {
  const entries = alloc?.entries || [];
  const hasContent = shift || entries.length > 0;

  if (isBH && !shift && !entries.length) {
    const theme = resolveBankHolidayTheme(bhName);
    const ThemeIcon = theme.Icon;
    return (
      <div
        className={`rounded-xl border ring-1 px-3 py-2 text-xs font-medium flex items-center gap-1.5 ${theme.className}`}
        data-testid="cell-bank-holiday"
        data-bh-name={bhName || undefined}
        data-bh-theme={theme.key}
        title={bhName || "Bank Holiday"}>
        <ThemeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{bhName || "Bank Holiday"}</span>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div
        className="rounded-xl border border-dashed border-[#2563EB]/40 bg-[#F0F5FF]/40 px-3 py-2 text-xs font-semibold text-[#1D4ED8] hover:border-[#2563EB] hover:bg-[#DBE7FE]/60 transition-colors"
        data-testid="cell-empty-affordance">
        {canEdit ? "+ Add shift" : "-"}
      </div>
    );
  }

  const statusColor = conflict.status === "error"
    ? "border-l-red-500 bg-red-50/60 ring-red-100"
    : conflict.status === "warn"
    ? "border-l-amber-500 bg-amber-50/50 ring-amber-100"
    : "border-l-[#2563EB] bg-[#DBE7FE]/40 ring-[#E4EAF4]";

  // Build colour bar for assignments
  const total = entries.reduce((s,e) => s + (e.minutes||0), 0) || 1;

  // Resolve a "primary" location for the cell when display
  // toggle is on. Precedence: shift.location_id → alloc.location_id →
  // first entry with a location_id. Distinct entry locations are listed
  // below as small chips. Existing rotas without any location_id render
  // exactly as before (showLocations is false by default per tenant).
  const locMap = showLocations
    ? Object.fromEntries(locations.map((l) => [l.location_id, l]))
    : {};
  const entryLocIds = showLocations
    ? Array.from(new Set(entries.map((e) => e.location_id).filter(Boolean)))
    : [];
  const primaryLocId = showLocations
    ? (shift?.location_id || alloc?.location_id || entryLocIds[0] || null)
    : null;
  const primaryLoc = primaryLocId ? locMap[primaryLocId] : null;

  // TP-P5-overnight (Feb 2026) -- treat end < start as a shift
  // that crosses midnight. We keep the cell in the START day
  // and surface an "Overnight · ends <day>" chip so managers
  // do not need to invent two rows.
  const _isOvernight = !!(shift && shift.start_time && shift.end_time
    && shift.end_time < shift.start_time);
  const _endsDayLabel = _isOvernight
    ? (() => {
        try {
          const d = new Date(day + "T00:00:00");
          d.setDate(d.getDate() + 1);
          return d.toLocaleDateString(undefined, { weekday: "short" });
        } catch { return "next day"; }
      })()
    : null;

  return (
    <div className={`rounded-xl border border-slate-200 border-l-4 ring-1 ${statusColor} px-3 py-2 shadow-[0_1px_2px_rgba(11,26,54,0.04)] hover:shadow-[0_4px_12px_rgba(11,26,54,0.08)] transition-shadow`} data-testid="shift-cell">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-slate-900 tracking-tight tabular-nums">
          {shift ? `${shift.start_time} - ${shift.end_time}` : <span className="text-amber-700">No shift</span>}
        </div>
        {conflict.status !== "ok" && (
          conflict.status === "error"
            ? <AlertCircle className="h-3.5 w-3.5 text-red-500"/>
            : <AlertTriangle className="h-3.5 w-3.5 text-amber-500"/>
        )}
      </div>
      {_isOvernight && (
        <div className="mt-0.5 text-[10px] font-medium text-indigo-700"
             data-testid="cell-overnight-badge"
             title={`Shift crosses midnight and ends ${_endsDayLabel} at ${shift.end_time}`}>
          Overnight · ends {_endsDayLabel} {shift.end_time}
        </div>
      )}
      {primaryLoc && (
        <div className="mt-1 flex items-center gap-1.5 flex-wrap"
              data-testid={`cell-location-${primaryLoc.location_id}`}
              title={primaryLoc.address || primaryLoc.name}>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-[#4B5773]">
            <span className="inline-block h-1.5 w-1.5 rounded-full"
                   style={{ backgroundColor: primaryLoc.colour || "#64748B" }} />
            {primaryLoc.name}
          </span>
          {entryLocIds
            .filter((id) => id !== primaryLoc.location_id)
            .slice(0, 2)
            .map((id) => {
              const l = locMap[id];
              if (!l) return null;
              return (
                <span key={id}
                  data-testid={`cell-location-${id}`}
                  title={l.address || l.name}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-[#4B5773]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full"
                         style={{ backgroundColor: l.colour || "#64748B" }} />
                  {l.name}
                </span>
              );
            })}
        </div>
      )}
      {entries.length > 0 ? (
        <>
          <div className="mt-1.5 flex h-1.5 w-full rounded-full overflow-hidden bg-slate-100">
            {entries.map((e, i) => {
              const cat = categories.find(c => c.category_id === e.category_id);
              return <div key={i} style={{ width: `${(e.minutes/total)*100}%`, backgroundColor: cat?.color || "#94a3b8" }} />;
            })}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {entries.slice(0,3).map((e, i) => (
              <span key={i} className="text-[10px] text-slate-600 leading-tight">
                {e.category_name?.split(" ")[0]} {Math.round((e.minutes/60)*10)/10}h
              </span>
            ))}
            {entries.length > 3 && <span className="text-[10px] text-slate-500">+{entries.length-3}</span>}
          </div>
        </>
      ) : (
        <div className="text-[10px] text-amber-700 mt-1">Shift but no assignments</div>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="roster-legend">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EEF2FB] ring-1 ring-[#C7D4F2] text-[#2C407A] text-[11px] font-medium" data-testid="legend-allocated">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2563EB]"/>Allocated OK
      </span>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[11px] font-medium" data-testid="legend-warning">
        <AlertTriangle className="h-3 w-3 text-amber-500"/>Warning
      </span>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 ring-1 ring-red-200 text-red-700 text-[11px] font-medium" data-testid="legend-conflict">
        <AlertCircle className="h-3 w-3 text-red-500"/>Conflict
      </span>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 ring-1 ring-sky-200 text-sky-800 text-[11px] font-medium" data-testid="legend-bank-holiday">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400"/>Bank Holiday
      </span>
    </div>
  );
}


// Premium top-of-grid summary strip. 100% derived from already-fetched
// in-scope state — no extra fetch, no new endpoint, no fake data.
// Counts: employees scheduled (≥ 1 shift this week), shifts this week,
// warnings, conflicts, bank-holiday calendar dates intersecting the
// current 7-day window. Visual style mirrors the Overview Phase 2 cards.
function RosterSummaryStrip({ employees, shifts, allocations, conflicts, bankHolidaysByEmp, weekDays }) {
  const weekIsoSet = new Set(weekDays.map((d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }));
  const employeesScheduled = (() => {
    const ids = new Set();
    for (const s of shifts) if (s.employee_id) ids.add(s.employee_id);
    return ids.size;
  })();
  let warnings = 0, conflictsCount = 0;
  for (const k in conflicts) {
    const c = conflicts[k];
    if (!c || !c.status) continue;
    if (c.status === "warn") warnings += 1;
    else if (c.status === "error") conflictsCount += 1;
  }
  const bhDates = new Set();
  for (const empId in (bankHolidaysByEmp || {})) {
    for (const iso of (bankHolidaysByEmp[empId] || [])) {
      if (weekIsoSet.has(iso)) bhDates.add(iso);
    }
  }
  // TP-RS.team.2 Phase 3 hotfix #2 -- the "Allocations" tile used to
  // count every `allocations` document, including legacy skeleton
  // rows with `entries: []` left behind by old apply flows. That
  // inflated the count after a failed Apply (e.g. "Allocations: 27"
  // when the visible roster was actually empty). The tile now only
  // counts allocation documents that have at least one entry.
  const allocationsWithEntries = allocations.filter(
    (a) => Array.isArray(a?.entries) && a.entries.length > 0);
  const tiles = [
    { key: "employees", label: "Employees", value: employeesScheduled, hint: `of ${employees.length}`, tone: "indigo", testid: "summary-tile-employees" },
    { key: "shifts", label: "Shifts", value: shifts.length, hint: "this week", tone: "indigo", testid: "summary-tile-shifts" },
    { key: "allocations", label: "Allocations", value: allocationsWithEntries.length, hint: "scheduled", tone: "slate", testid: "summary-tile-allocations" },
    { key: "warnings", label: "Warnings", value: warnings, hint: warnings === 1 ? "cell" : "cells", tone: "amber", testid: "summary-tile-warnings" },
    { key: "conflicts", label: "Conflicts", value: conflictsCount, hint: conflictsCount === 1 ? "cell" : "cells", tone: "red", testid: "summary-tile-conflicts" },
    { key: "bh", label: "Bank holidays", value: bhDates.size, hint: bhDates.size === 1 ? "day" : "days", tone: "sky", testid: "summary-tile-bank-holidays" },
  ];
  const toneClasses = {
    indigo: { ring: "ring-[#C7D4F2]", chip: "bg-[#EEF2FB] text-[#2C407A] ring-[#C7D4F2]", num: "text-[#0B1A36]" },
    slate:  { ring: "ring-slate-200", chip: "bg-slate-50 text-slate-600 ring-slate-200", num: "text-[#0B1A36]" },
    amber:  { ring: "ring-amber-200", chip: "bg-amber-50 text-amber-700 ring-amber-200", num: "text-amber-700" },
    red:    { ring: "ring-red-200",   chip: "bg-red-50 text-red-700 ring-red-200",       num: "text-red-700" },
    sky:    { ring: "ring-sky-200",   chip: "bg-sky-50 text-sky-700 ring-sky-200",       num: "text-sky-700" },
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4" data-testid="roster-summary-strip">
      {tiles.map((t) => {
        const tc = toneClasses[t.tone];
        return (
          <div
            key={t.key}
            data-testid={t.testid}
            className={`rounded-xl bg-white border border-[#E4EAF4] ring-1 ${tc.ring}/40 px-3 py-2.5 shadow-[0_1px_2px_rgba(11,26,54,0.04)]`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5775A6] font-semibold truncate">
                {t.label}
              </div>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${tc.chip.split(" ")[0].replace("bg-", "bg-")}`}></span>
            </div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className={`font-display text-xl ${tc.num} tabular-nums`} data-testid={`${t.testid}-value`}>{t.value}</span>
              <span className="text-[11px] text-[#4B5773]">{t.hint}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function BulkShiftDialog({ open, onOpenChange, weekStart, employees, teams = [], userRole, shiftTemplates = [], onApplied }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  // Phase 2A Step 8 - legacy `brk` state removed alongside the
  // visible Break (min) input. Break is now picked as an Away
  // assignment row; the POST mirrors `break_minutes` from the
  // sum of picked Away rows on send.
  const [days, setDays] = useState([0,1,2,3,4]);
  const [overrideLock, setOverrideLock] = useState(false);
  const [bulkCats, setBulkCats] = useState([]);
  // TP-AT.RS.8.3 (Feb 2026) -- the global "Work activity for
  // blank Work rows" filler was removed in favour of per-row
  // activity selection so Bulk apply shift matches the Roster
  // cell editor UX. Save-time validation refuses to apply until
  // every row carries a category (or combo).
  // New richer action modes (see backend bulk endpoint docstring).
  // add | replace | clear_assignments | clear_all | skip_empty
  const [actionMode, setActionMode] = useState("add");
  const [workCats, setWorkCats] = useState([]);
  const [bulkCombos, setBulkCombos] = useState([]);
  // Optional bulk-apply location.
  const [bulkLocations, setBulkLocations] = useState([]);
  const [bulkLocationId, setBulkLocationId] = useState("");
  const [allEmps, setAllEmps] = useState([]);
  const [teamFilter, setTeamFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  // TP-Readiness (Feb 2026) -- double-click guard for Bulk Apply
  // submit. Managers double-tapping the Apply button previously
  // fired two POST /shifts/bulk requests, creating duplicate
  // shifts. `submitting` is flipped true at the start of `apply`
  // and reset to false on BOTH success and error. The dialog stays
  // mounted between opens, so it is also reset when it reopens.
  const [submitting, setSubmitting] = useState(false);
  // June 2026 -- indexes of Work rows blocked at Apply because a
  // start or end time is missing. Drives the red field highlight.
  const [invalidTimeRows, setInvalidTimeRows] = useState([]);
  const isTeamAdmin = userRole === "team_admin";

  useEffect(() => {
    if (open) {
      // Fetch the full scope-of-caller employee list so the dialog isn't bound to
      // the roster's team filter. Backend already scopes team_admin to their team.
      // TP-AT.RS.8.4 (Feb 2026) -- use `schedulable_only=true` so
      // permission-only Workspace / Team Admin users and other
      // `include_in_roster: False` rows never reach the selector.
      // The additional `employment_status === "active"` client
      // filter (below in `visibleEmps`) closes the remaining gap
      // vs `/api/roster`, which uses BOTH filters.
      api.get("/employees?schedulable_only=true").then(r => {
        const list = r.data || [];
        setAllEmps(list);
        // Default to "all visible employees" so a Select-All click is implicit.
        // Same active filter as the visible list below.
        setSelectedIds(list
          .filter(e => (e.employment_status || "active") === "active")
          .map(e => e.employee_id));
      }).catch(() => {
        setAllEmps(employees || []);
        setSelectedIds((employees || []).map(e => e.employee_id));
      });
      // Phase 2A Step 8 - load Work AND Away so the Bulk Apply
      // Shift dialog matches the Roster cell editor model. Break
      // is just an Away category; managers add it from the same
      // assignment dropdown. Shadow `break_minutes` is mirrored
      // from any picked Away rows on send.
      api.get("/categories").then(r => setWorkCats(r.data.filter(c => (c.kind === "work" || c.kind === "away") && c.is_active)));
      api.get("/combos").then(r => setBulkCombos((r.data || []).filter(c => c.is_active !== false))).catch(() => setBulkCombos([]));
      api.get("/locations").then(r => setBulkLocations(r.data || [])).catch(() => setBulkLocations([]));
      setTeamFilter("all");
      setSearch("");
      // Defensive reset: the dialog component stays mounted, so a
      // stale `submitting` from a previous run must never survive
      // into a fresh open (stuck "Applying..." bug).
      setSubmitting(false);
      setInvalidTimeRows([]);
    }
  }, [open, employees]);

  const teamName = useMemo(() => Object.fromEntries(teams.map(t => [t.team_id, t.name])), [teams]);

  // Visible employees = scope-of-caller list ∩ team filter ∩ search
  // TP-AT.RS.8.4 (Feb 2026) -- also filter `employment_status`
  // so terminated / on-leave rows never appear even if their
  // `include_in_roster` flag is missing. Mirrors the `/api/roster`
  // grid's filter (`{"employment_status": "active", **_schedulable_filter()}`).
  const visibleEmps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allEmps.filter(e =>
      ((e.employment_status || "active") === "active") &&
      (teamFilter === "all" || e.team_id === teamFilter) &&
      (!q || e.name.toLowerCase().includes(q))
    );
  }, [allEmps, teamFilter, search]);

  const toggleEmp = (id) =>
    setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  const selectAllVisible = () => {
    const next = new Set(selectedIds);
    visibleEmps.forEach(e => next.add(e.employee_id));
    setSelectedIds(Array.from(next));
  };
  const clearAll = () => setSelectedIds([]);
  const removeSelected = (id) => setSelectedIds(selectedIds.filter(x => x !== id));

  const toggleDay = (d) =>
    setDays(days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort());
  const preset = (kind) => {
    if (kind === "week") setDays([0,1,2,3,4,5,6]);
    else if (kind === "weekday") setDays([0,1,2,3,4]);
    else if (kind === "weekend") setDays([5,6]);
  };

  const addCat = (kindHint) => {
    // TP-AT.RS.8.3 (Feb 2026) -- accept an optional kind hint
    // from a template-derived row or from the quick-add buttons.
    // Blank rows always carry `category_id: ""` and get an optional
    // `_wanted_kind` marker so the picker can filter. We
    // DELIBERATELY do not preselect a Work category -- Work
    // categories differ per business.
    // June 2026 -- new Work rows prefill their clock window from the
    // dialog's shift Start / End so "cover the whole shift" is the
    // explicit, visible default. Times stay editable; Apply refuses
    // any Work row missing a time (no silent 1-hour fallback).
    const isWork = kindHint === "work";
    const prefillStart = isWork ? (start || "") : "";
    const prefillEnd = isWork ? (end || "") : "";
    const prefillMins = isWork
      ? (_rowMinsFromClock(prefillStart, prefillEnd) || 0)
      : 60;
    setBulkCats([...bulkCats, {
      category_id: "", minutes: prefillMins,
      start_time: prefillStart, end_time: prefillEnd,
      _wanted_kind: (kindHint === "work" || kindHint === "away")
        ? kindHint : undefined,
    }]);
  };
  const removeCat = (i) => {
    setBulkCats(bulkCats.filter((_, idx) => idx !== i));
    setInvalidTimeRows([]);
  };

  // TP-AT.RS.8.3 -- derive minutes from a start / end pair so
  // the row summary + the send payload stay consistent when the
  // manager only edits the clock windows (no separate minutes
  // input in the normal view). Handles overnight rows the same
  // way `deriveBlankWorkGapEntries` does.
  const _rowMinsFromClock = (startHHMM, endHHMM) => {
    const t2m = (t) => {
      if (!t || typeof t !== "string") return null;
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      return m ? Number(m[1]) * 60 + Number(m[2]) : null;
    };
    const s = t2m(startHHMM);
    let e = t2m(endHHMM);
    if (s == null || e == null) return null;
    if (e <= s) e += 24 * 60;
    return e - s;
  };
  const updateCat = (i, field, v) => {
    const next = [...bulkCats];
    next[i] = { ...next[i], [field]: v };
    // TP-AT.RS.8.3 -- when the manager edits a clock window,
    // recompute minutes so the send payload + the duration chip
    // always reflect the visible times. Manual minute edits
    // still win (updateCat called with field="minutes" bypasses
    // this branch).
    if (field === "start_time" || field === "end_time") {
      const recomputed = _rowMinsFromClock(next[i].start_time, next[i].end_time);
      if (recomputed != null && recomputed > 0) next[i].minutes = recomputed;
      setInvalidTimeRows((prev) => prev.filter((x) => x !== i));
    }
    setBulkCats(next);
  };
  /** Patch multiple fields on one bulk row - used when toggling between
   *  category and combo so the picker value stays consistent. */
  const updateCatFields = (i, patch) => {
    const next = [...bulkCats]; next[i] = { ...next[i], ...patch }; setBulkCats(next);
  };
  /** Expand any combo row in `bulkCats` into atomic entries tagged with combo_id.
   *  Identical algorithm to RosterPage.expandComboEntry so a combo applied via
   *  bulk lands as the same atomic rows it would when edited day-by-day. */
  const expandBulkRowsForSend = (rows) => {
    const out = [];
    for (const r of rows) {
      // TP-AT.RS.8.3 -- prefer clock-derived minutes when both
      // start_time and end_time are present. The visible row no
      // longer exposes an editable minutes field so the send
      // always matches what the manager sees. Legacy rows that
      // only have `minutes` (no clock window) still round-trip.
      const clockMins = _rowMinsFromClock(r.start_time, r.end_time);
      const mins = Math.max(0, Math.round(
        Number(clockMins != null ? clockMins : (r.minutes || 0))));
      if (mins <= 0) continue;
      if (r.combo_id) {
        const combo = bulkCombos.find(c => c.combo_id === r.combo_id);
        if (!combo) continue;
        const slices = combo.components.map(comp => ({
          category_id: comp.category_id,
          raw: (mins * Number(comp.percentage)) / 100,
        }));
        const snapped = slices.map(s => ({ ...s, minutes: Math.max(0, Math.round(s.raw / 30) * 30) }));
        let diff = mins - snapped.reduce((s, x) => s + x.minutes, 0);
        if (diff !== 0) {
          const idx = snapped.reduce((best, s, i) => s.raw > snapped[best].raw ? i : best, 0);
          snapped[idx].minutes = Math.max(0, snapped[idx].minutes + diff);
        }
        for (const s of snapped.filter(s => s.minutes > 0)) {
          out.push({ category_id: s.category_id, minutes: s.minutes,
                     combo_id: combo.combo_id, combo_label: combo.name });
        }
      } else if (r.category_id) {
        // TP-AT.RS.8.2 -- pass through per-entry clock windows
        // when present so template-applied rows land with the
        // correct wall-clock times (matches single-cell editor).
        const entry = { category_id: r.category_id, minutes: mins };
        if (r.start_time && r.end_time) {
          entry.start_time = r.start_time;
          entry.end_time = r.end_time;
        }
        if (r.note) entry.note = r.note;
        out.push(entry);
      }
    }
    return out;
  };

  // Phase 2A Step 8 - shift capacity uses gross wall time. Each
  // bulk row (Work or Away) consumes its own minutes from this
  // gross window; the legacy `brk` deduction is no longer needed
  // because Away rows now carry their share explicitly. Shadow
  // `break_minutes` is mirrored on send from picked Away rows.
  // TP-P5-overnight (Feb 2026) -- overnight-safe: when end < start
  // the shift crosses midnight, so add 24h to keep Bulk Add capacity
  // math honest.
  const shiftCapacity = (() => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const raw = (eh*60+em) - (sh*60+sm);
    return raw < 0 ? raw + 24*60 : raw;
  })();
  const allocated = bulkCats.reduce((s, e) => s + Number(e.minutes||0), 0);

  // Pretty preview of selected employees (first 3 + counter)
  const selectedSummary = useMemo(() => {
    if (!selectedIds.length) return "No employees selected";
    if (selectedIds.length === allEmps.length && allEmps.length > 0) return `All ${allEmps.length} employees selected`;
    const names = allEmps.filter(e => selectedIds.includes(e.employee_id)).map(e => e.name);
    if (names.length <= 3) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
  }, [selectedIds, allEmps]);

  const isClearMode = actionMode === "clear_assignments" || actionMode === "clear_all";
  const needsTimes = !isClearMode;

  // Friendly labels for each mode (used in buttons, headings, summary)
  const MODE_META = {
    add:               { label: "Add",               cta: "Add assignments",       destructive: false, blurb: "Apply the shift and ADD selected assignments alongside anything already in the cell." },
    replace:           { label: "Replace",           cta: "Replace assignments",   destructive: true,  blurb: "Apply the shift and REPLACE every existing assignment in the cell. Leave-sourced entries are always preserved." },
    clear_assignments: { label: "Clear assignments", cta: "Clear assignments",     destructive: true,  blurb: "Remove all manual assignments from the cell. Shift times remain. Leave-sourced entries are preserved." },
    clear_all:         { label: "Clear shift + assignments", cta: "Clear shift + assignments", destructive: true, blurb: "Delete the shift and remove every manual assignment. Leave-sourced entries are preserved." },
    skip_empty:        { label: "Only empty cells",  cta: "Apply to empty cells",  destructive: false, blurb: "Apply only to cells with NO existing shift and NO manual assignments. Skip everything else." },
  };

  const apply = async (e) => {
    e.preventDefault();
    // TP-Readiness -- guard against double-submits. If a request is
    // already in flight, silently drop the extra click.
    if (submitting) return;
    if (!selectedIds.length || !days.length) {
      toast.error("Pick at least one employee and one day."); return;
    }
    // TP-AT.RS.8.3 -- explicit friendly toast when a blank Work
    // row (template-derived, _wanted_kind === "work" and empty
    // category) is still unfilled. Historically the generic
    // "Each assignment needs an activity" message was ambiguous
    // for template users.
    const hasBlankWork = bulkCats.some(
      c => c._wanted_kind === "work" && !c.category_id && !c.combo_id);
    if (hasBlankWork) {
      toast.error(
        "Pick an activity for every blank Work row before applying.");
      return;
    }
    // June 2026 -- every Work row must carry an explicit start AND
    // end time. Without this check a timeless Work row silently fell
    // back to the legacy 60-minute default in expandBulkRowsForSend.
    const missingTimeRows = needsTimes
      ? bulkCats.reduce((acc, c, idx) => {
          const isWorkRow = !c.combo_id && c.category_id
            && workCats.find(
              (w) => w.category_id === c.category_id)?.kind === "work";
          if (isWorkRow && (!c.start_time || !c.end_time)) acc.push(idx);
          return acc;
        }, [])
      : [];
    if (missingTimeRows.length) {
      setInvalidTimeRows(missingTimeRows);
      toast.error(
        "Add a start and end time for each work row before applying.");
      return;
    }
    setInvalidTimeRows([]);
    // A bulk row is "valid" when it has a category OR a combo AND minutes > 0.
    const validEntries = bulkCats.filter(c => (c.category_id || c.combo_id) && Number(c.minutes) > 0);
    if (needsTimes && bulkCats.length && validEntries.length !== bulkCats.length) {
      toast.error("Each assignment needs an activity (or combo) and minutes > 0."); return;
    }
    if (needsTimes && allocated > shiftCapacity) {
      if (!window.confirm(`Assignments total ${Math.round(allocated/60*10)/10}h, more than the shift capacity ${Math.round(shiftCapacity/60*10)/10}h. Apply anyway? Each affected day will show a conflict warning.`)) return;
    }
    // Destructive-action guard (replace, clear_assignments, clear_all)
    const meta = MODE_META[actionMode];
    if (meta.destructive) {
      const cells = selectedIds.length * days.length;
      const ok = window.confirm(
        `${meta.label.toUpperCase()} - this will affect up to ${cells} cell${cells === 1 ? "" : "s"} ` +
        `(${selectedIds.length} employee${selectedIds.length === 1 ? "" : "s"} × ${days.length} day${days.length === 1 ? "" : "s"}).\n\n` +
        `${meta.blurb}\n\nProceed?`
      );
      if (!ok) return;
    }
    const dates = days.map(o => {
      const d = new Date(weekStart); d.setDate(d.getDate() + o); return iso(d);
    });
    // Expand combo rows into atomic entries before sending so reports split
    // automatically into the underlying categories.
    const sendEntries = needsTimes ? expandBulkRowsForSend(validEntries) : [];
    // Phase 2A Step 8 - mirror shadow break_minutes from picked
    // Away rows. When no Away row is picked, send 0 so legacy
    // hidden break never silently shows up. When one or more Away
    // rows are picked, sum their minutes - this is what
    // `break_minutes` represents to downstream consumers that
    // haven't migrated to the entries model.
    const _p2aAwayMinsTotal = needsTimes
      ? validEntries.reduce((s, e) => {
          const cat = workCats.find((c) => c.category_id === e.category_id);
          return s + (cat?.kind === "away" ? (Number(e.minutes) || 0) : 0);
        }, 0)
      : 0;
    try {
      setSubmitting(true);
      const { data } = await api.post("/shifts/bulk", {
        employee_ids: selectedIds, work_dates: dates,
        start_time: needsTimes ? start : "00:00",
        end_time: needsTimes ? end : "00:00",
        break_minutes: _p2aAwayMinsTotal,
        entries: sendEntries,
        action_mode: actionMode,
        override_lock: overrideLock,
        location_id: bulkLocationId || null,
      });
      const skipParts = [];
      const sumSkip = (key) => data.per_employee.reduce((s,p) => s + (p[key]?.length || 0), 0);
      const sBh = sumSkip("skipped_bank_holiday"),
            sLv = sumSkip("skipped_full_day_leave"),
            sLk = sumSkip("skipped_locked"),
            sNe = sumSkip("skipped_non_empty");
      if (sBh) skipParts.push(`${sBh} bank holiday`);
      if (sLv) skipParts.push(`${sLv} on leave`);
      if (sLk) skipParts.push(`${sLk} locked`);
      if (sNe) skipParts.push(`${sNe} not empty`);
      // Compose a result toast that reflects the mode
      let main = "";
      if (actionMode === "clear_assignments") {
        main = `Cleared assignments on ${data.allocations_cleared} cell${data.allocations_cleared === 1 ? "" : "s"} across ${data.employees_touched} employee${data.employees_touched === 1 ? "" : "s"}`;
      } else if (actionMode === "clear_all") {
        main = `Cleared ${data.shifts_cleared} shift${data.shifts_cleared === 1 ? "" : "s"} and ${data.allocations_cleared} assignment cell${data.allocations_cleared === 1 ? "" : "s"} across ${data.employees_touched} employee${data.employees_touched === 1 ? "" : "s"}`;
      } else {
        main = `${data.shifts_applied} shift${data.shifts_applied === 1 ? "" : "s"} · ${data.allocations_applied} assignment cell${data.allocations_applied === 1 ? "" : "s"} across ${data.employees_touched} employee${data.employees_touched === 1 ? "" : "s"}`;
      }
      toast.success(`${main}${skipParts.length ? ` · skipped ${skipParts.join(", ")}` : ""}`);
      setSubmitting(false);
      onOpenChange(false); onApplied();
    } catch (err) {
      if (err.response?.status === 423) toast.error("Some weeks are locked - enable override or reopen them first.");
      else toast.error(formatError(err.response?.data?.detail));
      // Re-enable so a manager can retry after correcting the error.
      setSubmitting(false);
    }
  };

  const DAY_LABELS_BS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" fullscreenOnMobile>
        <DialogHeader><DialogTitle>Bulk apply shift</DialogTitle></DialogHeader>
        <form onSubmit={apply} className="space-y-5">
          {/* Employee picker - scalable: team filter + search + collapsible list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="eyebrow">Employees</Label>
              <span className="text-xs text-[#4B5773]" data-testid="bulk-selected-count">
                <span className="font-medium text-[#1F2A44]">{selectedIds.length}</span> of {allEmps.length} selected
              </span>
            </div>

            {/* Selected summary chip-row */}
            <button type="button" data-testid="bulk-toggle-employees"
              onClick={()=>setPickerOpen(o=>!o)}
              className="w-full flex items-center justify-between gap-2 px-3 h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-left">
              <span className={`text-sm truncate ${selectedIds.length ? "text-[#1F2A44]" : "text-slate-400"}`}>
                {selectedSummary}
              </span>
              <ChevronDown className={`h-4 w-4 text-[#4B5773] transition-transform ${pickerOpen ? "rotate-180" : ""}`}/>
            </button>

            {pickerOpen && (
              <div className="mt-2 border border-slate-200 rounded-lg bg-white" data-testid="bulk-employee-picker">
                {/* Filter & search row */}
                <div className="p-2 border-b border-slate-100 flex flex-wrap gap-2 items-center">
                  {!isTeamAdmin && teams.length > 0 && (
                    <Select value={teamFilter} onValueChange={setTeamFilter}>
                      <SelectTrigger data-testid="bulk-team-filter" className="h-9 w-44 rounded-md border-slate-200 text-sm">
                        <SelectValue placeholder="All teams"/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All teams</SelectItem>
                        {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Input value={search} onChange={(e)=>setSearch(e.target.value)}
                    placeholder="Search employees…"
                    data-testid="bulk-search"
                    className="h-9 flex-1 min-w-[160px] rounded-md border-slate-200 text-sm"/>
                  <button type="button" onClick={selectAllVisible} data-testid="bulk-select-visible"
                    className="text-xs px-2.5 py-1.5 rounded-md text-[#2563EB] hover:bg-[#DBE7FE]">
                    Select all visible ({visibleEmps.length})
                  </button>
                  <button type="button" onClick={clearAll} data-testid="bulk-clear"
                    className="text-xs px-2.5 py-1.5 rounded-md text-[#4B5773] hover:bg-slate-100">
                    Clear selection
                  </button>
                </div>

                {/* Scrollable employee list */}
                <div className="max-h-56 overflow-y-auto p-1">
                  {visibleEmps.length === 0 ? (
                    <div className="text-xs text-[#4B5773] p-3 text-center">
                      {search ? `No employees match "${search}"` :
                       teamFilter !== "all" ? "No employees in this team." :
                       "No employees available."}
                    </div>
                  ) : (
                    visibleEmps.map(e => (
                      <label key={e.employee_id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox"
                          checked={selectedIds.includes(e.employee_id)}
                          onChange={()=>toggleEmp(e.employee_id)}
                          data-testid={`bulk-emp-${e.employee_id}`}
                          className="accent-[#2563EB]"/>
                        <span className="text-sm flex-1 truncate">{e.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-slate-400">
                          {teamName[e.team_id] || ""}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {/* Selected chip list (so the user knows who's picked even outside current filter) */}
                {selectedIds.length > 0 && selectedIds.length < allEmps.length && (
                  <div className="border-t border-slate-100 p-2 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto" data-testid="bulk-selected-chips">
                    {allEmps.filter(e => selectedIds.includes(e.employee_id)).map(e => (
                      <span key={e.employee_id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8] text-[11px]">
                        {e.name}
                        <button type="button" onClick={()=>removeSelected(e.employee_id)}
                          data-testid={`bulk-chip-remove-${e.employee_id}`}
                          className="hover:text-red-600"><X className="h-3 w-3"/></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action mode picker - defines what this bulk action will DO */}
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="bulk-action-mode">
            <Label className="eyebrow">Action</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
              {["add", "replace", "clear_assignments", "clear_all", "skip_empty"].map((m) => (
                <button type="button" key={m} onClick={()=>setActionMode(m)}
                  data-testid={`bulk-mode-${m}`}
                  className={`text-xs px-2.5 py-2 rounded-md border text-left transition-colors leading-tight ${
                    actionMode === m
                      ? (MODE_META[m].destructive
                          ? "bg-red-50 border-red-300 text-red-700"
                          : "bg-[#DBE7FE] border-[#2563EB] text-[#1D4ED8]")
                      : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                  }`}>
                  <div className="font-medium">{MODE_META[m].label}</div>
                </button>
              ))}
            </div>
            <p className={`text-xs mt-2 ${MODE_META[actionMode].destructive ? "text-red-700" : "text-[#4B5773]"}`}>
              {MODE_META[actionMode].destructive && <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5"/>}
              {MODE_META[actionMode].blurb}
            </p>
          </div>

          {/* Shift times - hidden for clear modes. Phase 2A Step 8:
              the visible Break (min) input is removed. Managers add
              Break as an Away assignment row below; shadow
              `break_minutes` is mirrored on send. */}
          {needsTimes && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start"><Input type="time" step="1800" required value={start} data-testid="bulk-start"
              onChange={(e)=>setStart(e.target.value)} className="h-11 rounded-lg"/></Field>
            <Field label="End"><Input type="time" step="1800" required value={end} data-testid="bulk-end"
              onChange={(e)=>setEnd(e.target.value)} className="h-11 rounded-lg"/></Field>
          </div>
          )}

          {/* Optional bulk-apply location */}
          {needsTimes && bulkLocations.length > 0 && (
            <div data-testid="bulk-location-row">
              <Label className="eyebrow">Location (optional)</Label>
              <Select value={bulkLocationId || "none"}
                onValueChange={(v) => setBulkLocationId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-11 rounded-lg mt-1 w-full sm:w-72"
                                data-testid="bulk-location-select">
                  <SelectValue placeholder="- No location -" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- No location -</SelectItem>
                  {bulkLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}
                                 data-testid={`bulk-location-opt-${l.location_id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full"
                               style={{ background: l.colour || "#64748B" }} />
                        {l.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Days */}
          <div>
            <Label className="eyebrow">Days in week of {iso(weekStart)}</Label>
            <div className="flex gap-2 mt-2 mb-2 flex-wrap">
              <button type="button" data-testid="bulk-preset-week" onClick={()=>preset("week")} className="text-xs px-2.5 py-1 rounded-full bg-[#DBE7FE] text-[#1D4ED8] hover:bg-[#C7D7FF]">Entire week</button>
              <button type="button" data-testid="bulk-preset-weekday" onClick={()=>preset("weekday")} className="text-xs px-2.5 py-1 rounded-full bg-[#DBE7FE] text-[#1D4ED8] hover:bg-[#C7D7FF]">Mon - Fri</button>
              <button type="button" data-testid="bulk-preset-weekend" onClick={()=>preset("weekend")} className="text-xs px-2.5 py-1 rounded-full bg-[#DBE7FE] text-[#1D4ED8] hover:bg-[#C7D7FF]">Weekend</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS_BS.map((d, i) => (
                <button type="button" key={d} onClick={()=>toggleDay(i)} data-testid={`bulk-day-${d}`}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${days.includes(i) ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"}`}>{d}</button>
              ))}
            </div>
          </div>

          {/* Assignments (optional) - hidden for clear modes */}
          {needsTimes && (
          <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-4">
            {/* TP-AT.RS.8.2 (Feb 2026) -- Apply Shift Template in
                Bulk Add. UI-only prefill: picking a template
                sets start/end AND populates the assignments list
                with the same Break rows + blank Work gap rows
                the single-cell editor produces. Byte-stable
                fallback when no template is picked. */}
            {shiftTemplates.length > 0 && (
              <div className="rounded-lg bg-white border border-[#DBE7FE] p-3 mb-3 flex items-center gap-3 flex-wrap"
                   data-testid="bulk-shift-tpl-picker">
                <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] font-semibold">Apply template</span>
                <Select value="" onValueChange={(id) => {
                  const tpl = shiftTemplates.find(t => t.shift_template_id === id);
                  if (!tpl) return;
                  // Preserve envelope + break shadow fields.
                  setStart(tpl.start_time || "");
                  setEnd(tpl.end_time || "");
                  // Convert template entries into the bulk row shape:
                  // Break rows keep their category_id + start/end,
                  // blank Work gaps get `_wanted_kind: "work"` so
                  // the manager can pick one Work activity for
                  // all of them via the required dropdown below.
                  const tplEntries = (tpl.entries || []).map(e => ({
                    category_id: e.category_id || "",
                    combo_id: null,
                    combo_label: null,
                    minutes: Number(e.minutes),
                    start_time: e.start_time || null,
                    end_time: e.end_time || null,
                    note: e.note || null,
                  }));
                  const derived = deriveBlankWorkGapEntries(
                    tpl.start_time, tpl.end_time, tplEntries)
                    .map(r => ({
                      category_id: r.category_id || "",
                      combo_id: null,
                      minutes: Number(r.minutes || 0),
                      start_time: r.start_time || "",
                      end_time: r.end_time || "",
                      note: r.note || null,
                      _wanted_kind: r._wanted_kind,
                    }));
                  setBulkCats(derived);
                }}>
                  <SelectTrigger data-testid="bulk-shift-tpl-select" className="h-9 rounded-md border-slate-200 w-full sm:w-64">
                    <SelectValue placeholder="Pick a saved template..."/>
                  </SelectTrigger>
                  <SelectContent>
                    {shiftTemplates.filter(t => t.is_active !== false).map(t => (
                      <SelectItem key={t.shift_template_id} value={t.shift_template_id}
                                   data-testid={`bulk-shift-tpl-opt-${t.shift_template_id}`}>
                        {t.name} ({t.start_time}-{t.end_time})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-[#4B5773]">Prefills the shift shape. Break rows land as Away; blank Work rows below need a Work activity before Apply.</span>
              </div>
            )}
            {/* TP-AT.RS.8.3 (Feb 2026) -- the earlier global "Work
                activity for blank Work rows" panel was removed.
                Managers pick each blank Work row's activity via
                the row's own selector below, matching the
                single-cell Roster editor UX. Save-time validation
                still refuses to apply until every row has a
                category (or combo). */}
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Assignments (optional)</div>
              <span className={`text-xs ${allocated > shiftCapacity ? "text-red-600 font-medium" : "text-[#4B5773]"}`}>
                {Math.round(allocated/60*10)/10}h of {Math.round(shiftCapacity/60*10)/10}h
              </span>
            </div>
            <div className="space-y-2 mb-2">
              {bulkCats.map((c, i) => {
                const pickerValue = c.combo_id ? `combo:${c.combo_id}` : (c.category_id ? `cat:${c.category_id}` : "");
                const onPickerChange = (v) => {
                  if (v.startsWith("combo:")) {
                    const cid = v.slice(6);
                    updateCatFields(i, { combo_id: cid, category_id: "" });
                  } else if (v.startsWith("cat:")) {
                    updateCatFields(i, { category_id: v.slice(4), combo_id: null });
                  }
                };
                const combo = c.combo_id ? bulkCombos.find(x => x.combo_id === c.combo_id) : null;
                const mins = Number(c.minutes || 0);
                return (
                <div key={i} className="bg-white rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <Select value={pickerValue} onValueChange={onPickerChange}>
                      <SelectTrigger data-testid={`bulk-cat-${i}`} className="flex-1 h-9 rounded-md border-slate-200">
                        <SelectValue placeholder="Select activity"/>
                      </SelectTrigger>
                      <SelectContent>
                        {/* Phase 2A Step 8 - Work and Away rendered
                            in the same list; Away gets a small pill
                            so the manager can tell them apart.
                            TP-BulkKind (Feb 2026) -- filter by the
                            row's `_wanted_kind` hint (set by the new
                            "Add work" / "Add break / away" quick
                            buttons) so managers see only the
                            relevant categories. Rows without a hint
                            (legacy / template-derived without kind)
                            still show every Work + Away entry. */}
                        {workCats
                          .filter(wc => {
                            const wk = c._wanted_kind;
                            if (wk === "work") return wc.kind === "work";
                            if (wk === "away") return wc.kind === "away";
                            return true;
                          })
                          .map(wc => (
                          <SelectItem key={wc.category_id} value={`cat:${wc.category_id}`}>
                            <span className="inline-flex items-center gap-2">
                              {wc.name}
                              {wc.kind === "away" && (
                                <span
                                  className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 ring-1 ring-violet-200"
                                  data-testid={`bulk-cat-away-${wc.category_id}`}
                                >
                                  Away
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                        {bulkCombos.length > 0 && c._wanted_kind !== "away" && (
                          <>
                            <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#4B5773]">Combos</div>
                            {bulkCombos.map(cb => (
                              <SelectItem key={cb.combo_id} value={`combo:${cb.combo_id}`}>
                                <span className="inline-flex items-center gap-2">
                                  {cb.name}
                                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]">Combo</span>
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {/* TP-AT.RS.8.3 -- per-row Start / End clock
                        inputs live on the primary row now, next
                        to the activity picker. The editable
                        minutes field is hidden from the normal
                        view; duration is derived from the clock
                        window and shown as a small chip. Legacy
                        rows without a clock window still round
                        trip via `expandBulkRowsForSend`, which
                        falls back to `r.minutes`. */}
                    <Input type="time" step="1800" value={c.start_time || ""}
                      data-testid={`bulk-cat-start-${i}`}
                      aria-label="Start time"
                      onChange={(e)=>updateCat(i, "start_time", e.target.value)}
                      className={`h-9 w-[104px] rounded-md ${
                        invalidTimeRows.includes(i) && !c.start_time
                          ? "border-red-500 ring-1 ring-red-300" : ""}`}/>
                    <span className="text-[10px] text-[#94A3B8]">-</span>
                    <Input type="time" step="1800" value={c.end_time || ""}
                      data-testid={`bulk-cat-end-${i}`}
                      aria-label="End time"
                      onChange={(e)=>updateCat(i, "end_time", e.target.value)}
                      className={`h-9 w-[104px] rounded-md ${
                        invalidTimeRows.includes(i) && !c.end_time
                          ? "border-red-500 ring-1 ring-red-300" : ""}`}/>
                    <span data-testid={`bulk-cat-duration-${i}`}
                          className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${
                            mins > 0 ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-400"}`}>
                      {mins > 0 ? minsToHM(mins) : "set time"}
                    </span>
                    <Button type="button" variant="ghost" size="sm" onClick={()=>removeCat(i)} data-testid={`bulk-cat-remove-${i}`}><X className="h-4 w-4"/></Button>
                  </div>
                  {/* Combo breakdown preview - same algorithm as the day editor */}
                  {combo && mins > 0 && (
                    <div className="mt-2 text-[11px] text-[#4B5773] flex flex-wrap items-center gap-1.5" data-testid={`bulk-combo-breakdown-${i}`}>
                      <span className="uppercase tracking-widest text-[10px] text-[#1D4ED8] font-medium">Splits as</span>
                      {combo.components.map(cmp => {
                        const cName = workCats.find(x => x.category_id === cmp.category_id)?.name || "-";
                        const m = Math.round((mins * Number(cmp.percentage)) / 100);
                        return <span key={cmp.category_id} className="px-1.5 py-0.5 rounded-full bg-white border border-slate-200">{cmp.percentage}% {cName} · {m} min</span>;
                      })}
                    </div>
                  )}
                </div>
                );
              })}
              {/* TP-BulkKind (Feb 2026) -- discoverability parity
                  with the Roster single-cell shift editor. Two
                  explicit quick buttons - "Add work" and "Add
                  break / away" - replace the previous single
                  "Add assignment" button. Both call the same
                  addCat helper but tag the new row with a
                  `_wanted_kind` so the activity picker filters
                  to the relevant categories. No new data model,
                  no new endpoint. */}
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline"
                  onClick={() => addCat("work")}
                  data-testid="bulk-add-work-btn"
                  className="h-9 rounded-md border-dashed">
                  <Plus className="h-4 w-4 mr-1"/>Add work
                </Button>
                <Button type="button" variant="outline"
                  onClick={() => addCat("away")}
                  data-testid="bulk-add-away-btn"
                  className="h-9 rounded-md border-dashed">
                  <Plus className="h-4 w-4 mr-1"/>Add break / away
                </Button>
              </div>
            </div>
          </div>
          )}

          {/* TP-RS.team.2 -- the override-lock toggle has only ever
              had effect for company_admin / super_admin on the
              backend. Hide it entirely for Team Admin so they don't
              see a control that does nothing. Wording also updated
              to "Workspace Admin" to match the rest of the UI. */}
          {!isTeamAdmin && (
            <label className="flex items-center gap-2 text-sm text-[#4B5773]">
              <input type="checkbox" checked={overrideLock} onChange={(e)=>setOverrideLock(e.target.checked)}
                data-testid="bulk-override-lock" className="accent-[#2563EB]"/>
              Override lock on any locked weeks (Workspace Admin only)
            </label>
          )}
          <div className="text-xs text-[#4B5773]">Approved leave days, bank holidays and locked periods are skipped automatically.</div>

          {/* Summary line - always visible so admins know the blast radius before clicking */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid="bulk-summary">
            <span className="text-[#4B5773]">You&apos;re about to </span>
            <span className={MODE_META[actionMode].destructive ? "font-medium text-red-700" : "font-medium text-[#1D4ED8]"}>
              {MODE_META[actionMode].label.toLowerCase()}
            </span>
            <span className="text-[#4B5773]"> on </span>
            <span className="font-medium text-[#1F2A44]">{selectedIds.length} employee{selectedIds.length === 1 ? "" : "s"}</span>
            <span className="text-[#4B5773]"> × </span>
            <span className="font-medium text-[#1F2A44]">{days.length} day{days.length === 1 ? "" : "s"}</span>
            <span className="text-[#4B5773]"> = </span>
            <span className="font-medium text-[#1F2A44]">{selectedIds.length * days.length} cell{selectedIds.length * days.length === 1 ? "" : "s"}</span>
            <span className="text-[#4B5773]"> (before automatic skips).</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" data-testid="bulk-close-btn" onClick={()=>onOpenChange(false)}
              className="rounded-lg border-slate-200">Close</Button>
            <Button type="submit" data-testid="bulk-apply-btn" disabled={!selectedIds.length || !days.length || submitting}
              className={`rounded-lg disabled:opacity-50 ${MODE_META[actionMode].destructive ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"}`}>
              {submitting ? "Applying…" : MODE_META[actionMode].cta}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders a vertical, time-anchored preview of the day:
 *  09:00  Retention            240 min
 *  13:00  Break                  60 min
 *  14:00  Inbound              180 min
 *
 * Honours `break_start_time`. When break_start_time is empty the break is
 * shown at the end of the timeline as a lump-sum block so the totals still
 * add up but no positioning is implied.
 */
export function AssignmentTimeline({ shift, categories = [] }) {
  // TP-AT.RS.7.3 -- editor's Day Timeline is built from the
  // assignment entries ONLY. Legacy shift.break_minutes /
  // break_start_time are intentionally NOT forwarded here so they
  // can no longer visually split a Work row that has no Away entry.
  // On opening a legacy cell, RosterPage.openCell synthesises a
  // Break Away entry from break_minutes, which then becomes the
  // visible source of truth here.
  const items = buildTimeline({
    start: shift?.start_time,
    end: shift?.end_time,
    breakMinutes: 0,
    breakStart: null,
    entries: (shift?.entries || []).filter(e => Number(e.minutes) > 0),
    categories,
  });
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4" data-testid="assignment-timeline">
      <div className="eyebrow mb-2">Day timeline</div>
      <ul className="divide-y divide-slate-100">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 py-1.5">
            <span className="text-xs tabular-nums w-[5.5rem] text-[#1F2A44] font-medium pt-0.5">{it.start}{it.end ? `-${it.end}` : ""}</span>
            {it.kind === "break" ? (
              <span className="text-xs uppercase tracking-widest text-[#4B5773] flex-1 pt-0.5">Break</span>
            ) : (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: it.color }}/>
                  <span className="text-sm font-medium text-[#0B1A36] truncate">{it.label}</span>
                </div>
                {it.note && <div className="text-xs text-[#4B5773] mt-0.5">{it.note}</div>}
              </div>
            )}
            <span className="text-xs text-[#4B5773] tabular-nums pt-0.5">{it.minutes} min</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// Roster page nudge for pending drafts on visible days.
// Read-only informational pill; click navigates to the weekly review
// grid for the period that covers the visible week if there is one,
// otherwise falls back to the long /app/drafts queue.
//
// Exported so the Overview dashboard can render the SAME nudge for
// the current week without duplicating the logic or copy.
export function RosterDraftNudge({ weekDays, refreshKey = 0 }) {
  const [byDate, setByDate] = useState({});
  const [activePeriodId, setActivePeriodId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(
          (weekDays || []).map(async (d) => {
            try {
              const { data } = await api.get(`/drafts/by-day/${d}`);
              return [d, data];
            } catch { return [d, null]; }
          })
        );
        if (cancelled) return;
        const counts = {};
        let period = null;
        for (const [d, data] of entries) {
          counts[d] = data?.pending_count || 0;
          if (!period && data?.active_period?.period_id) {
            period = data.active_period.period_id;
          }
        }
        setByDate(counts);
        setActivePeriodId(period);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [weekDays?.join(","), refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = Object.values(byDate).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  const target = activePeriodId
    ? `/app/drafts/rota/${activePeriodId}`
    : "/app/drafts";
  const linkLabel = activePeriodId
    ? `Open the draft rota for review`
    : `review on the Draft assignments page`;

  return (
    <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-dashed border-amber-300 flex items-start gap-2"
         data-testid="roster-draft-nudge">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium text-amber-900 px-2 py-0.5 rounded bg-amber-100 border border-amber-300 mt-0.5 shrink-0">
        Draft · Not live
      </span>
      <div className="text-xs text-[#0B1A36] flex-1 leading-relaxed">
        <button type="button"
                onClick={() => navigate(target)}
                className="font-medium text-amber-900 hover:underline"
                data-testid="roster-draft-nudge-link">
          {total} draft shift{total === 1 ? "" : "s"} pending for review
        </button>
        {activePeriodId ? (
          <>
            {", "}
            <button type="button"
                    onClick={() => navigate(target)}
                    className="text-amber-900 hover:underline"
                    data-testid="roster-draft-nudge-rota-link">
              {linkLabel}
            </button>
            {". Drafts never appear on this roster until you approve and publish them."}
          </>
        ) : (
          <>
            {", "}{linkLabel}. Drafts never appear on this roster until you Approve them into a draft rota and then Publish.
          </>
        )}
      </div>
    </div>
  );
}
